/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Button, Card, Chip, CircularProgress, Grid,
  IconButton, Table, TableBody, TableCell, TableContainer, TableRow,
  TextField, Typography, Select, MenuItem, FormControlLabel, Switch,
  Tooltip, Alert, Autocomplete, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import MDDatePicker from 'components/MDDatePicker';
import MDMonthYearPicker from 'components/MDMonthYearPicker';
import CreatableSelect from 'components/CreatableSelect';
import { api } from '../../../api';
import { usePharmacy } from '../../../contexts/PharmacyContext';
import irmHeader from 'assets/images/irm-header.png';
import { codeBarcodeStack, barcodeScriptBlock, documentCodeValue } from 'utils/printBarcodes';
import TimelineDialog from '../components/TimelineDialog';
import printSupplierPayment from './printSupplierPayment';
import SupplierQuickAdd from './quickAdd/SupplierQuickAdd';
import LocationQuickAdd from './quickAdd/LocationQuickAdd';
import ItemQuickAdd from './quickAdd/ItemQuickAdd';
import KeyboardHintBar from 'components/KeyboardHintBar';
import { headerEnterNav, focusRef } from 'utils/formKeyboard';

// ── Theme constants ─────────────────────────────────────────────
const TEAL = '#00798E';
const TEAL_LIGHT = '#EBF6F8';

// ── Column count for tab navigation ────────────────────────────
const NCOLS = 11; // item(0) variant(1) batch(2) mfg(3) expiry(4) units(5) sub-units(6) mrp(7) price(8) disc(9) cess(10)

// ── Status colour maps ──────────────────────────────────────────
const STATUS_COLOR = { DRAFT: 'default', RECEIVED: 'success', CANCELLED: 'error', PARTIALLY_RETURNED: 'warning', SETTLED: 'success' };
const PAY_STATUS_COLOR = { PENDING: 'warning', PARTIAL: 'info', PAID: 'success' };
const TXN_STATUS_COLOR = { PAID: 'success', CANCELLED: 'error', ADJUSTED: 'default' };

// ── Shared style helpers ────────────────────────────────────────
const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtExpiry = (v) => {
  if (!v) return '—';
  const [yr, mo] = String(v).split('-');
  return (yr && mo) ? `${MONTHS[parseInt(mo, 10) - 1]} ${yr}` : v;
};
const fmtMonth = fmtExpiry;

const gridInputSx = (extra = {}) => ({
  '& .MuiOutlinedInput-root': {
    height: 30, fontSize: '0.78rem', borderRadius: 0,
    '& fieldset': { border: '1px solid transparent' },
    '&:hover fieldset': { borderColor: '#ddd' },
    '&.Mui-focused fieldset': { borderColor: TEAL, borderWidth: 1 },
  },
  '& .MuiOutlinedInput-input': { padding: '3px 5px', ...extra },
});

// Date-picker grid cells: calendar icon only appears while the field is focused;
// out of focus it reads as a plain value with no button chrome.
const gridDateInputSx = () => ({
  ...gridInputSx(),
  '& .MuiInputAdornment-root': { display: 'none' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiInputAdornment-root, & .MuiOutlinedInput-root:focus-within .MuiInputAdornment-root': { display: 'flex' },
});

const TH = {
  py: 0.75, px: 0.75, fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.05em', color: 'rgba(255,255,255,0.95) !important', bgcolor: TEAL,
  borderBottom: '1.5px solid rgba(0,0,0,0.12)', whiteSpace: 'nowrap',
};
const TD = { py: 0, px: 0, borderBottom: '1px solid #f2f2f2' };

// ── Data helpers ────────────────────────────────────────────────
const emptyRow = () => ({
  _key: `${Date.now()}-${Math.random()}`,
  _itemData: null,
  _itemName: '',
  _variants: [],
  _variant_name: '',
  _sub_units_per_base: 1,
  _base_unit: '',
  _sub_unit: '',
  _has_sub_unit: false,
  _hsn_code: '',
  irm_pharm_items_id: '',
  irm_pharm_item_variants_id: '',
  batch_no: '',
  manufacturing_date: '',
  expiry_date: '',
  qty_units: '',
  qty_sub_units: '',
  mrp: '',
  purchase_price: '',
  discount_percentage: '0',
  gst_percentage: '',
  cess_percentage: '0',
});

const calcLineBreakdown = (row, isInterstate = false) => {
  const qty   = parseFloat(row.qty_units) || 0;
  const mrp   = parseFloat(row.mrp) || 0;
  const price = parseFloat(row.purchase_price) || 0;
  const disc  = parseFloat(row.discount_percentage) || 0;
  const gst   = parseFloat(row.gst_percentage) || 0;
  const cess  = parseFloat(row.cess_percentage) || 0;
  const mrpBase        = gst > 0 ? mrp / (1 + gst / 100) : mrp;
  // purchase_price IS the net per-unit cost. `disc` is only a "% below MRP"
  // margin indicator (shown on the grid / print) — it is NOT subtracted
  // again here; doing so double-counted it (MRP 100 / price 70 → 49).
  const taxablePerUnit = price;
  const taxable        = qty * taxablePerUnit;
  const gstAmt         = taxable * gst / 100;
  const cessAmt        = taxable * cess / 100;
  const lineTotal      = taxable + gstAmt + cessAmt;
  const cgst = isInterstate ? 0 : gstAmt / 2;
  const sgst = isInterstate ? 0 : gstAmt / 2;
  const igst = isInterstate ? gstAmt : 0;
  return { qty, mrp, mrpBase, price, disc, gst, cess, taxablePerUnit, taxable, gstAmt, cgst, sgst, igst, cessAmt, lineTotal };
};

const calcLineTotal = (row, isInterstate) => calcLineBreakdown(row, isInterstate).lineTotal;

const calcPriceSynopsis = (items, isInterstate = false, roundOffEnabled = true) => {
  const byRate = {};
  let grossValue = 0;
  let totalDiscount = 0;
  items.forEach((r) => {
    const qty   = parseFloat(r.qty_units) || 0;
    const price = parseFloat(r.purchase_price) || 0;
    const mrp   = parseFloat(r.mrp) || 0;
    const gst   = parseFloat(r.gst_percentage) || 0;
    const cess  = parseFloat(r.cess_percentage) || 0;
    if (!r.irm_pharm_items_id || qty === 0 || price === 0) return;
    const gross   = qty * price;
    // "Discount vs MRP" — informational only, never deducted from the payable.
    const mrpBase = gst > 0 ? mrp / (1 + gst / 100) : mrp;
    const discAmt = qty * Math.max(0, mrpBase - price);
    const taxable = gross;
    const gstAmt  = taxable * gst / 100;
    const cessAmt = taxable * cess / 100;
    grossValue    += gross;
    totalDiscount += discAmt;
    const key = String(gst);
    if (!byRate[key]) {
      byRate[key] = { gst, cgstRate: isInterstate ? 0 : gst / 2, sgstRate: isInterstate ? 0 : gst / 2, igstRate: isInterstate ? gst : 0, taxable: 0, cgst: 0, sgst: 0, igst: 0, cess: 0, gstTotal: 0, lineTotal: 0 };
    }
    byRate[key].taxable   += taxable;
    byRate[key].cgst      += isInterstate ? 0 : gstAmt / 2;
    byRate[key].sgst      += isInterstate ? 0 : gstAmt / 2;
    byRate[key].igst      += isInterstate ? gstAmt : 0;
    byRate[key].cess      += cessAmt;
    byRate[key].gstTotal  += gstAmt;
    byRate[key].lineTotal += taxable + gstAmt + cessAmt;
  });
  const rates      = Object.values(byRate).sort((a, b) => a.gst - b.gst);
  const totalTaxable = rates.reduce((s, r) => s + r.taxable, 0);
  const totalCgst  = rates.reduce((s, r) => s + r.cgst, 0);
  const totalSgst  = rates.reduce((s, r) => s + r.sgst, 0);
  const totalIgst  = rates.reduce((s, r) => s + r.igst, 0);
  const totalCess  = rates.reduce((s, r) => s + r.cess, 0);
  const totalGst   = rates.reduce((s, r) => s + r.gstTotal, 0);
  const rawNetPayable = totalTaxable + totalGst + totalCess;
  const roundedNetPayable = Math.round(rawNetPayable);
  const roundOff = roundOffEnabled ? parseFloat((roundedNetPayable - rawNetPayable).toFixed(2)) : 0;
  const netPayable = roundOffEnabled ? roundedNetPayable : parseFloat(rawNetPayable.toFixed(2));
  return { rates, grossValue, totalDiscount, totalTaxable, totalCgst, totalSgst, totalIgst, totalCess, totalGst, rawNetPayable, roundOff, netPayable };
};

function amountInWordsGrn(amount) {
  if (!amount && amount !== 0) return '';
  const n = Math.abs(Math.round(amount * 100));
  const paise = n % 100; const rupees = Math.floor(n / 100);
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const toW = (num) => {
    if (num === 0) return '';
    if (num < 20) return ones[num];
    if (num < 100) return tens[Math.floor(num/10)] + (num%10 ? ' '+ones[num%10] : '');
    return ones[Math.floor(num/100)] + ' Hundred' + (num%100 ? ' '+toW(num%100) : '');
  };
  const inIndian = (num) => {
    if (num === 0) return 'Zero'; let out = '';
    if (num >= 10000000) { out += toW(Math.floor(num/10000000))+' Crore '; num %= 10000000; }
    if (num >= 100000)   { out += toW(Math.floor(num/100000))  +' Lakh ';  num %= 100000; }
    if (num >= 1000)     { out += toW(Math.floor(num/1000))    +' Thousand '; num %= 1000; }
    if (num > 0)         { out += toW(num); } return out.trim();
  };
  let out = inIndian(rupees);
  if (paise > 0) out += ` and ${toW(paise)} Paise`;
  return 'INR ' + out.trim();
}

function printGrn(invoice, items, ph) {
  if (!invoice) return;
  const PRIMARY = '#005a6e';
  const LIGHT_BG = '#eaf6f8';
  const BORDER = '#b2d8de';
  const isInterstate = invoice._is_interstate || (Number(invoice.total_igst) > 0 && Number(invoice.total_cgst) === 0);
  const sup = invoice.supplier || {};
  const fmtD = (d) => { if (!d) return '—'; return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); };
  const C = `border:1px solid ${BORDER};padding:3px 5px;font-size:7pt;`;
  const CH = `${C}background:${LIGHT_BG};color:${PRIMARY};font-weight:700;text-align:center;`;

  const { html: grnBarcodeHtml, initScript: grnBarcodeInit } = codeBarcodeStack({
    idPrefix: 'bc-grn',
    items: [{ label: 'GRN No', code: invoice.inward_no, transform: documentCodeValue }],
  });

  const validItems = items.filter(r => r.irm_pharm_items_id && (parseFloat(r.qty_units)||0) > 0 && (parseFloat(r.purchase_price)||0) > 0);
  const roundOffOn = invoice.round_off_enabled !== 'N';
  const synopsis = calcPriceSynopsis(validItems, isInterstate, roundOffOn);

  const itemCards = validItems.map((it, i) => {
    const bd = calcLineBreakdown(it, isInterstate);
    const gstLabel = isInterstate
      ? `IGST ${bd.gst.toFixed(1)}%: <b style="font-family:monospace">₹${bd.igst.toFixed(2)}</b>`
      : `CGST ${(bd.gst / 2).toFixed(1)}% + SGST ${(bd.gst / 2).toFixed(1)}%: <b style="font-family:monospace">₹${(bd.cgst + bd.sgst).toFixed(2)}</b>`;
    return `
    <div style="border:1px solid ${BORDER};border-radius:3px;margin-bottom:3px;padding:4px 6px;${i % 2 !== 0 ? `background:${LIGHT_BG}66` : ''}">
      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:6px">
        <div style="flex:1;min-width:0;font-size:7.8pt">
          <span style="font-weight:700;color:${PRIMARY}">${i + 1}.</span>
          <span style="font-weight:700">${it._itemName || ''}</span>${it._variant_name ? ` <span style="font-weight:400;color:#666;font-size:7pt">(${it._variant_name})</span>` : ''}
        </div>
        <div style="font-weight:800;font-family:monospace;font-size:8.3pt;color:${PRIMARY};white-space:nowrap">₹${bd.lineTotal.toFixed(2)}</div>
      </div>
      <div style="font-size:6.3pt;color:#888;font-family:monospace;margin-top:1px">Batch ${it.batch_no || '—'} · Mfg ${fmtMonth(it.manufacturing_date)} · Exp ${fmtExpiry(it.expiry_date)} · HSN ${it._hsn_code || '—'}</div>
      <div style="display:flex;flex-wrap:wrap;column-gap:10px;row-gap:1px;font-size:6.7pt;color:#555;margin-top:3px;border-top:1px dashed ${BORDER};padding-top:2px">
        <span>Qty <b style="font-family:monospace">${bd.qty.toFixed(2)} ${it._base_unit || 'NOS'}</b></span>
        <span>MRP <b style="font-family:monospace">₹${bd.mrp.toFixed(2)}</b></span>
        <span>Purch. Price <b style="font-family:monospace">₹${bd.price.toFixed(2)}</b></span>
        ${bd.disc > 0 ? `<span>Disc vs MRP <b style="font-family:monospace">${bd.disc.toFixed(1)}%</b></span>` : ''}
        <span>Taxable <b style="font-family:monospace">₹${bd.taxable.toFixed(2)}</b></span>
        <span>${gstLabel}</span>
      </div>
    </div>`;
  }).join('');

  const gstRows = synopsis.rates.map(g => isInterstate
    ? `<tr><td style="${C}text-align:right;font-family:monospace">${g.taxable.toFixed(2)}</td><td style="${C}text-align:center">—</td><td style="${C}text-align:right">—</td><td style="${C}text-align:center">${g.igstRate.toFixed(1)}%</td><td style="${C}text-align:right;font-family:monospace">${g.igst.toFixed(2)}</td><td style="${C}text-align:right;font-family:monospace;font-weight:700">${(g.taxable+g.igst).toFixed(2)}</td></tr>`
    : `<tr><td style="${C}text-align:right;font-family:monospace">${g.taxable.toFixed(2)}</td><td style="${C}text-align:center">${g.cgstRate.toFixed(1)}%</td><td style="${C}text-align:right;font-family:monospace">${g.cgst.toFixed(2)}</td><td style="${C}text-align:center">${g.sgstRate.toFixed(1)}%</td><td style="${C}text-align:right;font-family:monospace">${g.sgst.toFixed(2)}</td><td style="${C}text-align:right;font-family:monospace;font-weight:700">${(g.taxable+g.cgst+g.sgst).toFixed(2)}</td></tr>`
  ).join('');

  const body = `
    <div style="margin-bottom:6px">
      <div style="line-height:0">
        <img src="${irmHeader}" alt="Company Header" style="width:100%;object-fit:contain;display:block">
      </div>
      <div style="text-align:center;margin-top:5px">
        <div style="font-size:14pt;font-weight:700;letter-spacing:1.5px;font-family:'Montserrat',Arial,sans-serif;color:${PRIMARY}">GOODS RECEIPT NOTE</div>
        ${isInterstate ? `<div style="margin-top:2px"><span style="font-size:7pt;font-weight:700;color:#c0392b;border:1px solid #c0392b;padding:1px 6px;border-radius:2px">INTERSTATE / IGST</span></div>` : ''}
      </div>
    </div>
    <table style="width:100%;border-collapse:collapse;border:1.5px solid ${BORDER};margin-bottom:0">
      <tr>
        <td style="width:50%;border-right:1px solid ${BORDER};padding:5px 8px;vertical-align:top">
          <div style="font-size:12pt;font-weight:900;color:${PRIMARY};line-height:1.1">${ph?.name || 'PHARMACY'}</div>
          ${ph?.sub_title ? `<div style="font-size:8pt;color:#444;margin-top:1px">${ph.sub_title}</div>` : ''}
          <div style="font-size:7.5pt;color:#333;margin-top:2px;line-height:1.6">${ph?.address||''}${ph?.city?', '+ph.city:''}${ph?.pincode?' - '+ph.pincode:''}</div>
          ${ph?.dl_no ? `<div style="font-size:7pt;color:#555">DL No : ${ph.dl_no}</div>` : ''}
          ${ph?.gstin ? `<div style="font-size:7pt;color:#555">GSTIN : ${ph.gstin}</div>` : ''}
          ${ph?.gstin_uin && ph.gstin_uin !== ph.gstin ? `<div style="font-size:7pt;color:#555">GSTIN/UIN : ${ph.gstin_uin}</div>` : ''}
          ${ph?.state ? `<div style="font-size:7pt;color:#555">State Name : ${ph.state}${ph.state_code?', Code : '+ph.state_code:''}</div>` : ''}
          ${ph?.email ? `<div style="font-size:7pt;color:#555">E-Mail : ${ph.email}</div>` : ''}
          ${ph?.mobile ? `<div style="font-size:7pt;color:#555">Mobile : ${ph.mobile}</div>` : ''}
        </td>
        <td style="width:50%;padding:5px 8px;vertical-align:top">
          <table style="width:100%;border-collapse:collapse;font-size:7.5pt">
            <tr><td style="color:#777;padding-bottom:3px;padding-right:6px;white-space:nowrap">GRN No.</td><td style="font-weight:700;padding-bottom:3px">${invoice.inward_no}</td></tr>
            <tr><td style="color:#777;padding-bottom:3px;padding-right:6px;white-space:nowrap">Received Date</td><td style="font-weight:700;padding-bottom:3px">${fmtD(invoice.received_date)}</td></tr>
            <tr><td style="color:#777;padding-bottom:3px;padding-right:6px;white-space:nowrap">Supplier Inv. No.</td><td style="font-weight:600;padding-bottom:3px">${invoice.supplier_invoice_no||'—'}</td></tr>
            <tr><td style="color:#777;padding-right:6px;white-space:nowrap">Supplier Inv. Date</td><td style="font-weight:600">${fmtD(invoice.supplier_invoice_date)}</td></tr>
          </table>
          ${grnBarcodeHtml ? `<div style="margin-top:4px">${grnBarcodeHtml}</div>` : ''}
        </td>
      </tr>
      <tr style="border-top:1px solid ${BORDER}">
        <td colspan="2" style="padding:4px 8px;font-size:7.5pt;vertical-align:top">
          <span style="font-size:7pt;color:${PRIMARY};font-weight:700;text-transform:uppercase">Supplier: </span>
          <span style="font-weight:700">${sup.name||'—'}</span>
          ${sup.gst_no ? `<span style="font-size:7pt;color:#777;margin-left:8px">GSTIN: ${sup.gst_no}</span>` : ''}
          ${sup.address ? `<span style="font-size:7pt;color:#777;margin-left:8px">${sup.address}${sup.city?', '+sup.city:''}</span>` : ''}
        </td>
      </tr>
    </table>
    ${barcodeScriptBlock(grnBarcodeInit)}

    <div style="border-top:1.5px solid ${PRIMARY};padding-top:4px;margin-top:2px">
      ${itemCards}
    </div>
    <table style="width:100%;border-collapse:collapse;margin-top:2px;border:1px solid ${BORDER}">
      ${roundOffOn && Math.abs(synopsis.roundOff) >= 0.005 ? `
      <tr>
        <td style="${C}font-size:7.5pt;color:#555">Sub Total</td>
        <td style="${C}text-align:right;font-family:monospace;font-size:7.5pt;width:110px">₹${synopsis.rawNetPayable.toFixed(2)}</td>
      </tr>
      <tr>
        <td style="${C}font-size:7.5pt;color:#555">Round Off</td>
        <td style="${C}text-align:right;font-family:monospace;font-size:7.5pt;width:110px">${synopsis.roundOff >= 0 ? '+' : '−'}₹${Math.abs(synopsis.roundOff).toFixed(2)}</td>
      </tr>` : ''}
      <tr style="background:${LIGHT_BG};font-weight:700">
        <td style="${C}font-weight:800;font-size:8pt">Total (Taxable ₹${synopsis.totalTaxable.toFixed(2)})</td>
        <td style="${C}text-align:right;font-family:monospace;font-weight:800;font-size:8.5pt;width:110px">₹${synopsis.netPayable.toFixed(2)}</td>
      </tr>
    </table>

    <table style="width:100%;border-collapse:collapse;margin-top:0;border:1px solid ${BORDER};border-top:none;font-size:7.5pt">
      <tr>
        <td style="padding:4px 7px;border-right:1px solid ${BORDER};font-weight:700;color:#555;white-space:nowrap;font-size:7pt">Amount Chargeable (in words)</td>
        <td style="padding:4px 10px;font-style:italic">${amountInWordsGrn(synopsis.netPayable)} Only</td>
        <td style="padding:4px 7px;border-left:1px solid ${BORDER};font-size:7pt;color:#888;text-align:right;white-space:nowrap">E. &amp; O.E.</td>
      </tr>
    </table>

    <div style="display:flex;justify-content:flex-end;margin-top:5px">
      <div style="width:60%">
        <div style="font-size:7pt;font-weight:700;color:${PRIMARY};text-transform:uppercase;letter-spacing:0.5px;margin-bottom:2px">Tax Analysis</div>
        <table style="width:100%;border-collapse:collapse;font-size:7pt;table-layout:fixed">
          <tr>
            <th style="${CH}">Taxable Value</th>
            <th style="${CH}">CGST Rate</th><th style="${CH}">CGST Amt</th>
            <th style="${CH}">SGST Rate</th><th style="${CH}">SGST Amt</th>
            ${isInterstate ? `<th style="${CH}">IGST Rate</th><th style="${CH}">IGST Amt</th>` : ''}
            <th style="${CH}">Total Tax Amt</th>
          </tr>
          ${gstRows}
          <tr style="background:${LIGHT_BG};font-weight:700">
            <td style="${C}text-align:right;font-family:monospace">${synopsis.totalTaxable.toFixed(2)}</td>
            <td style="${C}text-align:center">—</td>
            <td style="${C}text-align:right;font-family:monospace">${isInterstate ? '—' : synopsis.totalCgst.toFixed(2)}</td>
            <td style="${C}text-align:center">—</td>
            <td style="${C}text-align:right;font-family:monospace">${isInterstate ? '—' : synopsis.totalSgst.toFixed(2)}</td>
            ${isInterstate ? `<td style="${C}text-align:center">—</td><td style="${C}text-align:right;font-family:monospace">${synopsis.totalIgst.toFixed(2)}</td>` : ''}
            <td style="${C}text-align:right;font-family:monospace;font-weight:700">${synopsis.totalGst.toFixed(2)}</td>
          </tr>
        </table>
        <div style="margin-top:3px;font-size:7.5pt;font-style:italic"><b>Tax Amount (in words):</b> ${amountInWordsGrn(synopsis.totalGst)} Only</div>
      </div>
    </div>

    <table style="width:100%;border-collapse:collapse;margin-top:5px;border:1px solid ${BORDER};font-size:7.5pt">
      <tr>
        <td style="width:45%;vertical-align:top;border-right:1px solid ${BORDER};padding:5px 7px">
          ${ph?.pan_no ? `<div style="margin-bottom:3px"><b>Company&apos;s PAN:</b> <span style="font-family:monospace;letter-spacing:0.8px">${ph.pan_no}</span></div>` : ''}
          ${ph?.declaration ? `<div style="font-size:6.5pt;font-weight:700;color:${PRIMARY};text-transform:uppercase;margin-top:${ph.pan_no?'4px':'0'};margin-bottom:2px">Declaration</div><div style="font-size:6.5pt;color:#555;line-height:1.5">${ph.declaration}</div>` : ''}
        </td>
        <td style="width:30%;vertical-align:top;border-right:1px solid ${BORDER};padding:5px 7px">
          ${ph?.bank_name ? `
            <div style="font-size:6.5pt;font-weight:700;color:${PRIMARY};text-transform:uppercase;margin-bottom:3px">Company&apos;s Bank Details</div>
            <table style="font-size:7pt;border-collapse:collapse;width:100%">
              <tr><td style="color:#777;padding-bottom:2px;white-space:nowrap;padding-right:4px">Bank Name</td><td style="font-weight:600;padding-bottom:2px">: ${ph.bank_name}</td></tr>
              ${ph.account_no ? `<tr><td style="color:#777;padding-bottom:2px;white-space:nowrap;padding-right:4px">A/c No.</td><td style="font-weight:600;padding-bottom:2px">: ${ph.account_no}</td></tr>` : ''}
              ${ph.bank_branch ? `<tr><td style="color:#777;padding-bottom:2px;white-space:nowrap;padding-right:4px">Branch</td><td style="font-weight:600;padding-bottom:2px">: ${ph.bank_branch}</td></tr>` : ''}
              ${ph.ifsc_code ? `<tr><td style="color:#777;padding-bottom:2px;white-space:nowrap;padding-right:4px">IFS Code</td><td style="font-weight:600;padding-bottom:2px">: ${ph.ifsc_code}</td></tr>` : ''}
            </table>
          ` : ''}
        </td>
        <td style="width:25%;vertical-align:bottom;text-align:center;padding:5px 7px;padding-top:18px">
          <div style="font-size:7.5pt;font-weight:700;color:#333;margin-bottom:18px">for ${ph?.name||''}</div>
          <div style="border-top:1.5px solid #333;padding-top:4px;font-size:8pt;font-weight:700">Authorised Signatory</div>
        </td>
      </tr>
      <tr style="border-top:1px solid ${BORDER}">
        <td colspan="2" style="padding:5px 7px;border-right:1px solid ${BORDER};vertical-align:middle">
          <div style="height:36px;border:1px dashed #ccc;border-radius:3px;display:flex;align-items:center;justify-content:center;color:#ccc;font-style:italic;font-size:7pt">Authorised Seal</div>
        </td>
        <td style="padding:5px 7px;text-align:center;vertical-align:middle;font-size:6.5pt;color:#aaa;font-style:italic">
          This is a Computer Generated Document
        </td>
      </tr>
    </table>
  `;

  const win = window.open('', '_blank', 'width=960,height=720');
  const toolbar = `<div id="ptb" style="position:fixed;top:0;left:0;right:0;background:#005a6e;color:#fff;padding:9px 16px;display:flex;justify-content:space-between;align-items:center;z-index:9999;font-family:Arial,sans-serif;box-shadow:0 2px 8px rgba(0,0,0,0.25)"><span style="font-weight:700;font-size:10.5pt">Goods Receipt Note &mdash; ${invoice.inward_no}</span><div style="display:flex;gap:8px"><button onclick="window.print()" style="background:#fff;color:#005a6e;border:none;padding:7px 16px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#128438; Print</button><button onclick="window.print()" style="background:rgba(255,255,255,0.18);color:#fff;border:1px solid rgba(255,255,255,0.5);padding:7px 16px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#8595; Download PDF</button><button onclick="window.close()" style="background:transparent;color:rgba(255,255,255,0.8);border:1px solid rgba(255,255,255,0.4);padding:7px 10px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#10005;</button></div></div>`;
  win.document.write(`<!DOCTYPE html><html><head><title>GRN ${invoice.inward_no}</title>
    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&display=swap">
    <style>@page{size:A5 portrait;margin:8mm}*{box-sizing:border-box}body{font-family:'Montserrat',Arial,sans-serif;margin:0;padding:50px 0 0 0;-webkit-print-color-adjust:exact;print-color-adjust:exact}td,th{overflow-wrap:break-word}@media print{#ptb{display:none!important}body{padding-top:0!important}}</style>
    </head><body>${toolbar}<div class="pf-page" style="width:499px;margin:0 auto">${body}</div>
    <script>window.onload=function(){try{var els=document.querySelectorAll('.pf-page');for(var i=0;i<els.length;i++){var h=els[i].scrollHeight;if(h>733)els[i].style.zoom=Math.max(0.55,733/h);}}catch(e){}};</script>
    </body></html>`);
  win.document.close();
}

const emptyPaymentForm = () => ({
  amount: '', payment_mode: '',
  payment_date: new Date().toISOString().substring(0, 10),
  utr_number: '', notes: '',
});

// ════════════════════════════════════════════════════════════════
//  MICRO-COMPONENTS (reusable across sections)
// ════════════════════════════════════════════════════════════════

/** Labelled field wrapper — for header form fields */
function FieldLabel({ label, children }) {
  return (
    <Box>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.4 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

/** Stat tile — payment summary cards */
function StatTile({ label, val, color, bg = '#f8f9fa', border = '#eee' }) {
  return (
    <Box sx={{ p: 1.5, bgcolor: bg, borderRadius: 1.5, textAlign: 'center', border: `1px solid ${border}` }}>
      <Typography sx={{ fontSize: '0.6rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</Typography>
      <Typography sx={{ fontSize: '1rem', fontWeight: 800, color, fontFamily: 'monospace' }}>{val}</Typography>
    </Box>
  );
}

/** Unit input — shows  [input] | UnitName  inline in one cell */
function UnitInput({ value, onChange, unitName, disabled, inputRef, onKeyDown }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, gap: 0.4 }}>
      <TextField
        size="small" value={value} onChange={onChange} disabled={disabled}
        inputRef={inputRef} onKeyDown={onKeyDown}
        sx={{ width: 54, flexShrink: 0, ...gridInputSx({ textAlign: 'right' }) }}
        inputProps={{ tabIndex: -1 }}
      />
      {unitName ? (
        <>
          <Typography sx={{ fontSize: '0.6rem', color: '#ccc', lineHeight: 1, flexShrink: 0 }}>|</Typography>
          <Typography sx={{ fontSize: '0.76rem', fontFamily: 'monospace', color: '#444', whiteSpace: 'nowrap', lineHeight: 1, flexShrink: 0 }}>
            {unitName}
          </Typography>
        </>
      ) : null}
    </Box>
  );
}

/** GST badge chip */
function GstBadge({ value }) {
  if (!value) return <Typography sx={{ fontSize: '0.7rem', color: '#ccc', px: 0.5, lineHeight: '30px' }}>—</Typography>;
  return (
    <Chip label={`${value}%`} size="small"
      sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700, bgcolor: '#e8f5e9', color: '#2e7d32' }} />
  );
}

/** Numeric table cell input */
function GridNumCell({ value, onChange, onBlur, disabled, inputRef, onKeyDown, align = 'right' }) {
  return (
    <TextField size="small" fullWidth value={value} onChange={onChange} onBlur={onBlur}
      disabled={disabled} inputRef={inputRef} onKeyDown={onKeyDown}
      sx={gridInputSx({ textAlign: align })} inputProps={{ tabIndex: -1 }} />
  );
}

/** Round a string number to 2 decimal places; returns '' if invalid */
const round2 = (v) => { const n = parseFloat(v); return isNaN(n) ? v : n.toFixed(2); };

/** Teal gradient section header bar */
function SectionHeader({ title, action }) {
  return (
    <Box sx={{ background: `linear-gradient(135deg, ${TEAL} 0%, #005469 100%)`, px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography sx={{ color: 'white !important', fontWeight: 700, fontSize: '0.85rem', flexGrow: 1 }}>{title}</Typography>
      {action}
    </Box>
  );
}

/** Synopsis label-value row */
function SynRow({ label, val, color, bold, large, divTop }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.55, borderTop: divTop ? `2px solid ${TEAL}30` : '1px solid rgba(0,0,0,0.05)' }}>
      <Typography sx={{ fontSize: large ? '0.85rem' : '0.78rem', color: color || '#555', fontWeight: bold ? 700 : 400 }}>{label}</Typography>
      <Typography sx={{ fontFamily: 'monospace', fontSize: large ? '0.9rem' : '0.8rem', fontWeight: bold ? 800 : 600, color: color || '#333' }}>{val}</Typography>
    </Box>
  );
}

// ── Item autocomplete search cell ──────────────────────────────
function ItemCell({ row, onSelect, readOnly, cellRef, onTabKey, onCreateNew }) {
  const [opts, setOpts] = useState([]);
  const [inputVal, setInputVal] = useState(row._itemName || '');
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => { if (!row._itemData) setInputVal(''); }, [row._itemData]);

  const handleInputChange = (_, val, reason) => {
    setInputVal(val);
    if (reason === 'reset') return;
    clearTimeout(timer.current);
    if (!val || val.trim().length < 1) { setOpts([]); setLoading(false); return; }
    // Mark "busy" from the first keystroke — not just once the request is in
    // flight — so the "➕ Create" row can never surface (and get auto-selected)
    // while a search for the current text is still pending.
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const res = await api.pharmacy.listItems({ search: val.toUpperCase(), limit: 20 });
        setOpts(Array.isArray(res.data) ? res.data : []);
      } catch { setOpts([]); }
      finally { setLoading(false); }
    }, 250);
  };

  const query = inputVal.trim();
  const currentName = (row._itemData?.name || '').toUpperCase();
  // Offer "➕ Create" only once a search for the *current* text has settled and
  // came back without the drug — never mid-search, never on bare focus, and not
  // when the text still matches the already-picked item.
  const canCreate = !!onCreateNew && !readOnly && !loading
    && query.length >= 2 && query.toUpperCase() !== currentName;

  const filterOptions = (x) => {
    if (loading) return [];
    const list = [...x];
    if (canCreate) {
      list.push({ __create: true, id: '__create__', name: `Create "${query}"`, _typed: query });
    }
    return list;
  };

  return (
    <Box
      onKeyDown={(e) => {
        if (e.key === 'Tab') {
          e.preventDefault(); e.stopPropagation();
          onTabKey(e.shiftKey ? -1 : 1);
          return;
        }
        // While the dropdown is open let the Autocomplete own Enter (select the
        // highlighted item / create row). Only hijack Enter to advance to the
        // next cell when no listbox is open.
        if (e.key === 'Enter') {
          if (e.target?.getAttribute?.('aria-expanded') === 'true') return;
          e.preventDefault(); e.stopPropagation();
          onTabKey(1);
        }
      }}
      sx={{ width: '100%' }}
    >
      <Autocomplete
        value={row._itemData} inputValue={inputVal} options={opts} loading={loading}
        autoHighlight openOnFocus
        filterOptions={filterOptions}
        getOptionLabel={(o) => (typeof o === 'object' && o ? o.name : String(o || ''))}
        isOptionEqualToValue={(o, v) => o?.id === v?.id}
        onInputChange={handleInputChange}
        onChange={(_, selected) => {
          if (!selected) return;
          if (selected.__create) { onCreateNew && onCreateNew(selected._typed || query); return; }
          setInputVal((selected.name || '').toUpperCase()); onSelect(selected);
        }}
        noOptionsText={
          loading ? 'Searching…'
            : query.length < 1 ? 'Type to search…'
            : onCreateNew && !readOnly
              ? <Box component="span" sx={{ color: TEAL, fontWeight: 700, fontSize: '0.76rem', cursor: 'pointer' }}
                  onMouseDown={(e) => { e.preventDefault(); onCreateNew(query); }}>
                  ➕ Create {query ? `"${query}"` : 'new item'}
                </Box>
              : 'No items found'
        }
        disabled={readOnly}
        componentsProps={{ popper: { sx: { zIndex: 9999 } } }}
        renderOption={(props, o) => (
          <Box component="li" {...props} key={o.__create ? '__create' : o.id}
            sx={o.__create
              ? { fontSize: '0.8rem', py: '6px !important', color: TEAL, fontWeight: 700, borderTop: '1px solid #eee' }
              : { fontSize: '0.78rem', py: '6px !important' }}>
            {o.__create ? `➕ ${o.name}` : (
              <>
                <Box sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{o.name}</Box>
                <Box sx={{ fontFamily: 'monospace', fontSize: '0.64rem', color: '#aaa' }}>
                  {o.item_code}{o.generic_name ? ` · ${o.generic_name}` : ''}{o.hsn_code?.hsn_code ? ` · HSN ${o.hsn_code.hsn_code}` : ''}
                </Box>
              </>
            )}
          </Box>
        )}
        renderInput={(params) => (
          <TextField {...params} size="small" placeholder="Type to search…" inputRef={cellRef} sx={gridInputSx({ textAlign: 'left' })} />
        )}
      />
    </Box>
  );
}

// ── Price Breakdown Dialog ──────────────────────────────────────
function PriceBreakdownDialog({ open, onClose, row, isInterstate }) {
  if (!row) return null;
  const bd = calcLineBreakdown(row, isInterstate);
  const hasData = bd.price > 0 || bd.mrp > 0;

  const BdRow = ({ label, val, color, bold, indent, divTop }) => (
    <Box sx={{ display: 'flex', py: 0.5, borderTop: divTop ? `2px solid ${TEAL}30` : '1px solid rgba(0,0,0,0.04)' }}>
      <Typography sx={{ flex: 1, fontSize: indent ? '0.68rem' : '0.74rem', color: color || (indent ? '#999' : '#555'), fontWeight: bold ? 700 : 400, pl: indent ? 2 : 0 }}>{label}</Typography>
      <Typography sx={{ fontFamily: 'monospace', fontSize: '0.74rem', fontWeight: bold ? 700 : 500, color: color || '#333', minWidth: 95, textAlign: 'right' }}>{val}</Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ background: `linear-gradient(135deg, ${TEAL} 0%, #005469 100%)`, px: 2.5, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'white !important' }}>Price Breakdown</Typography>
            <Typography sx={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.78) !important', mt: 0.3 }}>
              {row._itemName || 'Item'}{row._hsn_code ? ` · HSN ${row._hsn_code}` : ''}{bd.gst > 0 ? ` · GST ${bd.gst}%` : ''}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)', mt: -0.5 }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: '20px !important', pb: 1 }}>
        {/* Meta strip */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1.5, border: '1px solid #eee' }}>
          {row.batch_no && <Box><Typography variant="caption" color="text.secondary" display="block">Batch</Typography><Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{row.batch_no}</Typography></Box>}
          {row.manufacturing_date && <Box><Typography variant="caption" color="text.secondary" display="block">Mfg Date</Typography><Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{fmtMonth(row.manufacturing_date)}</Typography></Box>}
          {row.expiry_date && <Box><Typography variant="caption" color="text.secondary" display="block">Expiry</Typography><Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{fmtExpiry(row.expiry_date)}</Typography></Box>}
          {bd.qty > 0 && <Box><Typography variant="caption" color="text.secondary" display="block">Qty</Typography><Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{bd.qty}{row._base_unit ? ` ${row._base_unit}` : ''}</Typography></Box>}
          {row._has_sub_unit && row.qty_sub_units && <Box><Typography variant="caption" color="text.secondary" display="block">{row._sub_unit || 'Sub-units'}</Typography><Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{row.qty_sub_units} {row._sub_unit}</Typography></Box>}
          {row._hsn_code && <Box><Typography variant="caption" color="text.secondary" display="block">HSN Code</Typography><Typography sx={{ fontWeight: 600, fontSize: '0.82rem', fontFamily: 'monospace', color: TEAL }}>{row._hsn_code}</Typography></Box>}
        </Box>

        {!hasData ? (
          <Typography sx={{ color: '#aaa', fontSize: '0.82rem', textAlign: 'center', py: 3 }}>
            Enter MRP and Purchase Price to see the breakdown.
          </Typography>
        ) : (
          <>
            {bd.mrp > 0 && (
              <>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>MRP Analysis</Typography>
                <BdRow label="MRP (GST-inclusive, printed on pack)" val={fmt(bd.mrp)} bold />
                {bd.gst > 0 && <>
                  <BdRow label={`Less: GST included in MRP (${bd.gst}%)`} val={`−${fmt(bd.mrp - bd.mrpBase)}`} color="#c62828" indent />
                  <BdRow label="MRP Base / unit (ex-GST)" val={fmt(bd.mrpBase)} color={TEAL} />
                </>}
                {row._has_sub_unit && bd.mrp > 0 && row._sub_units_per_base > 1 && (
                  <BdRow label={`MRP per ${row._sub_unit} (÷${row._sub_units_per_base})`} val={fmt(bd.mrp / row._sub_units_per_base)} color="#555" indent />
                )}
              </>
            )}

            {bd.price > 0 && (
              <>
                <Box sx={{ mt: 1.5, mb: 0.5 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Purchase Calculation</Typography>
                </Box>
                <BdRow label="Purchase Price / unit (ex-GST)" val={fmt(bd.price)} />
                {bd.disc > 0 && <BdRow label={`Discount vs MRP (${bd.disc}%) — info only`} val={fmt(Math.max(0, bd.mrpBase - bd.price))} color="#888" indent />}
                <BdRow label="Taxable Value / unit" val={fmt(bd.taxablePerUnit)} color="#1565c0" bold />
                {bd.gst > 0 && (
                  isInterstate
                    ? <BdRow label={`Add: IGST (${bd.gst}%)`} val={`+${fmt(bd.taxablePerUnit * bd.gst / 100)}`} color="#2e7d32" indent />
                    : <>
                        <BdRow label={`Add: CGST (${bd.gst / 2}%)`} val={`+${fmt(bd.taxablePerUnit * bd.gst / 200)}`} color="#2e7d32" indent />
                        <BdRow label={`Add: SGST (${bd.gst / 2}%)`} val={`+${fmt(bd.taxablePerUnit * bd.gst / 200)}`} color="#2e7d32" indent />
                      </>
                )}
                {bd.cess > 0 && <BdRow label={`Add: Cess (${bd.cess}%)`} val={`+${fmt(bd.taxablePerUnit * bd.cess / 100)}`} color="#6a1b9a" indent />}
                {row._has_sub_unit && row._sub_units_per_base > 1 && bd.taxablePerUnit > 0 && (
                  <BdRow label={`Taxable / ${row._sub_unit} (÷${row._sub_units_per_base})`} val={fmt(bd.taxablePerUnit / row._sub_units_per_base)} color="#888" indent />
                )}
              </>
            )}

            {bd.qty > 0 && bd.price > 0 && (
              <>
                <Box sx={{ mt: 1.5, mb: 0.5 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Line Total — {bd.qty} {row._base_unit || 'unit'}{bd.qty !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                <BdRow label="Taxable subtotal" val={fmt(bd.taxable)} color="#1565c0" />
                {isInterstate
                  ? <BdRow label={`IGST (${bd.gst}%)`} val={fmt(bd.igst)} color="#2e7d32" indent />
                  : <>
                      <BdRow label={`CGST (${bd.gst / 2}%)`} val={fmt(bd.cgst)} color="#2e7d32" indent />
                      <BdRow label={`SGST (${bd.gst / 2}%)`} val={fmt(bd.sgst)} color="#2e7d32" indent />
                    </>
                }
                {bd.cess > 0 && <BdRow label={`Cess (${bd.cess}%)`} val={fmt(bd.cessAmt)} color="#6a1b9a" indent />}
                <BdRow divTop label="Net Line Total" val={fmt(bd.lineTotal)} color={TEAL} bold />
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, borderTop: '1px solid #f0f0f0' }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: `${TEAL} !important`, fontWeight: 600 }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ════════════════════════════════════════════════════════════════
//  MAIN FORM
// ════════════════════════════════════════════════════════════════
function InwardForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;
  const { selectedPharmacy } = usePharmacy();

  const [invoice, setInvoice] = useState(null);
  const [loadingInv, setLoadingInv] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [locations, setLocations] = useState([]);

  const [header, setHeader] = useState({
    irm_pharm_suppliers_id: '',
    irm_pharm_store_locations_id: '',
    supplier_invoice_no: '',
    supplier_invoice_date: '',
    received_date: new Date().toISOString().substring(0, 10),
    notes: '',
    round_off_enabled: 'Y',
    _is_interstate: false,
  });

  const [items, setItems] = useState([emptyRow()]);

  // Payment
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [paymentTxns, setPaymentTxns] = useState([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingTxnId, setEditingTxnId] = useState(null);
  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm());
  const [savingPayment, setSavingPayment] = useState(false);
  const [podFile, setPodFile] = useState(null);
  const podInputRef = useRef(null);

  // Price breakdown dialog
  const [priceDialogRow, setPriceDialogRow] = useState(null);

  // GRN Documents
  const [grnDocs, setGrnDocs] = useState([]);
  const [showDocsDialog, setShowDocsDialog] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docType, setDocType] = useState('OTHER');
  const docFileRef = useRef(null);

  // Timeline
  const [showTimeline, setShowTimeline] = useState(false);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  // GRN Return dialog
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [existingReturns, setExistingReturns] = useState([]);
  const [returnLines, setReturnLines] = useState([]);
  const [returnReason, setReturnReason] = useState('');
  const [returnDate, setReturnDate] = useState(new Date().toISOString().substring(0, 10));
  const [savingReturn, setSavingReturn] = useState(false);

  const cellRefs = useRef([]);
  const readOnly = invoice && invoice.status !== 'DRAFT';

  // ── Inline "Add New" quick-add dialogs (Supplier / Location / Item) ──
  const [quickAdd, setQuickAdd] = useState(null); // { kind:'supplier'|'location'|'item', name, rowIdx }
  const quickAddOpen = !!quickAdd;
  // Header field refs for Enter-to-next-field navigation
  const supRef = useRef(null);
  const locRef = useRef(null);
  const invNoRef = useRef(null);
  const invDateRef = useRef(null);
  const recvDateRef = useRef(null);
  // late-bound action refs so the global-shortcut effect can call handlers
  // that are declared further down the component
  const saveDraftRef = useRef(null);
  const confirmGrnRef = useRef(null);
  const focusEl = focusRef;
  const hdrEnter = (nextRef, toGrid) => headerEnterNav(
    toGrid ? () => setTimeout(() => cellRefs.current[0]?.[0]?.focus(), 0) : nextRef,
  );

  // ── Reference data ────────────────────────────────────────
  useEffect(() => {
    api.pharmacy.listSuppliers({ is_active: 'Y', limit: 200 }).then(r => setSuppliers(r.data || [])).catch(() => {});
    const locParams = selectedPharmacy ? { pharmacy_id: selectedPharmacy.id } : {};
    api.pharmacy.listLocations(locParams).then(r => setLocations(r.data || [])).catch(() => {});
  }, [selectedPharmacy]);

  // ── Fetch invoice ─────────────────────────────────────────
  const fetchInvoice = useCallback(async () => {
    if (isNew) return;
    setLoadingInv(true);
    try {
      const res = await api.pharmacy.getInward(id);
      const inv = res.data;
      setInvoice(inv);
      setHeader({
        irm_pharm_suppliers_id: String(inv.irm_pharm_suppliers_id || ''),
        irm_pharm_store_locations_id: String(inv.irm_pharm_store_locations_id || ''),
        supplier_invoice_no: inv.supplier_invoice_no || '',
        supplier_invoice_date: inv.supplier_invoice_date?.substring(0, 10) || '',
        received_date: inv.received_date?.substring(0, 10) || '',
        notes: inv.notes || '',
        round_off_enabled: inv.round_off_enabled === 'N' ? 'N' : 'Y',
        _is_interstate: (Number(inv.total_igst) > 0 && Number(inv.total_cgst) === 0),
      });
      if (inv.items?.length) {
        setItems(inv.items.map((it) => {
          const subPer = it.item?.sub_units_per_base || 1;
          const baseUnit =
            it.item?.base_unit?.short_name || it.item?.base_unit?.name ||
            it.item?.base_unit_name || it.item?.unit?.short_name ||
            it.item?.unit?.name   || it.item?.unit_name ||
            it.base_unit?.short_name || it.base_unit?.name || '';
          const subUnit  =
            it.item?.sub_unit?.short_name  || it.item?.sub_unit?.name  ||
            it.item?.sub_unit_name  || it.item?.piece_unit?.short_name ||
            it.item?.piece_unit?.name ||
            it.sub_unit?.short_name || it.sub_unit?.name || '';
          // fallback: if no distinct sub unit, treat as same as base (subPer=1, same unit name)
          const effectiveSubUnit = subUnit || baseUnit;
          const hsnCode =
            it.item?.hsn_code?.hsn_code || it.item?.hsn_code?.code ||
            (typeof it.item?.hsn_code === 'string' ? it.item.hsn_code : '') ||
            it.item?.hsn?.hsn_code || it.item?.hsn_code_value || '';
          return {
            _key: String(it.id),
            _itemData: it.item ? { id: it.item.id, name: it.item.name, item_code: it.item.item_code } : null,
            _itemName: (it.item?.name || '').toUpperCase(),
            _variants: it.item?.variants || [],
            _variant_name: it.variant?.variant_name
              || it.item_variant?.variant_name
              || (it.item?.variants || []).find(v => String(v.id) === String(it.irm_pharm_item_variants_id))?.variant_name
              || '',
            _sub_units_per_base: subPer,
            _base_unit: baseUnit,
            _sub_unit: effectiveSubUnit,
            _has_sub_unit: true, // always show sub-unit; when subPer=1 it mirrors qty
            _hsn_code: hsnCode,
            irm_pharm_items_id: String(it.irm_pharm_items_id || ''),
            irm_pharm_item_variants_id: it.irm_pharm_item_variants_id ? String(it.irm_pharm_item_variants_id) : '',
            batch_no: it.batch_no || '',
            manufacturing_date: it.manufacturing_date?.substring(0, 7) || '',
            expiry_date: it.expiry_date?.substring(0, 7) || '',
            qty_units: String(it.qty_received || ''),
            qty_sub_units: String(Math.round((it.qty_received || 0) * subPer)), // always set
            mrp: String(it.mrp || ''),
            purchase_price: String(it.purchase_price || ''),
            discount_percentage: String(it.discount_percentage || '0'),
            gst_percentage: String(it.gst_percentage || ''),
            cess_percentage: String(it.cess_percentage || '0'),
          };
        }));
      }
      if (inv.payment) {
        setPaymentSummary({ amount_due: inv.payment.amount_due, payment_status: inv.payment.payment_status });
      }
      if (inv.payment_transactions?.length) {
        setPaymentTxns(inv.payment_transactions);
      } else if (inv.payment && (parseFloat(inv.payment.amount_paid) || 0) > 0) {
        setPaymentTxns([{
          id: 'legacy', amount: inv.payment.amount_paid,
          payment_mode: inv.payment.payment_mode || '',
          payment_date: inv.payment.payment_date?.substring(0, 10) || '',
          utr_number: inv.payment.utr_number || inv.payment.cheque_number || '',
          notes: inv.payment.finance_notes || '',
          status: 'PAID', _legacy: true,
        }]);
      } else { setPaymentTxns([]); }
    } catch { navigate('/pharmacy/inward'); }
    finally { setLoadingInv(false); }
  }, [id, isNew, navigate]);

  const loadDocuments = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await api.pharmacy.listInwardDocuments(id);
      setGrnDocs(res.data || []);
    } catch { /* non-fatal */ }
  }, [id, isNew]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);
  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  // ── Tab navigation ────────────────────────────────────────
  const moveFocus = useCallback((rowIdx, colIdx, dir) => {
    let nextCol = colIdx + dir;
    let nextRow = rowIdx;
    let steps = 0;
    while (steps < NCOLS * 2) {
      if (nextCol >= NCOLS) { nextCol = 0; nextRow += 1; }
      if (nextCol < 0) { nextCol = NCOLS - 1; nextRow -= 1; }
      if (nextRow < 0) return;
      if (nextRow >= items.length) {
        const targetRow = nextRow;
        setItems(prev => {
          const next = [...prev, emptyRow()];
          setTimeout(() => cellRefs.current[targetRow]?.[0]?.focus(), 60);
          return next;
        });
        return;
      }
      // Column 1 (Variant) is a mouse-only dropdown — its default is auto-picked
      // on item select, and a <Select> in the tab path strands keyboard focus.
      if (nextCol === 1) { nextCol += dir; steps++; continue; }
      const el = cellRefs.current[nextRow]?.[nextCol];
      if (el) { el.focus(); return; }
      nextCol += dir;
      steps++;
    }
  }, [items.length]);

  const makeCellRef = (rowIdx, colIdx) => (el) => {
    if (!cellRefs.current[rowIdx]) cellRefs.current[rowIdx] = [];
    cellRefs.current[rowIdx][colIdx] = el;
  };

  const cellKeyDown = (rowIdx, colIdx) => (e) => {
    if (e.key === 'Tab' || e.key === 'Enter') { e.preventDefault(); moveFocus(rowIdx, colIdx, e.shiftKey ? -1 : 1); }
  };

  // ── Row update with auto-discount calc ────────────────────
  const updateRow = (rowIdx, patch) => {
    setItems(prev => {
      const next = [...prev];
      const row = { ...next[rowIdx], ...patch };
      if (('mrp' in patch || 'purchase_price' in patch) && !('discount_percentage' in patch)) {
        const m = parseFloat(row.mrp) || 0;
        const p = parseFloat(row.purchase_price) || 0;
        const g = parseFloat(row.gst_percentage) || 0;
        if (m > 0 && p > 0) {
          const mBase = g > 0 ? m / (1 + g / 100) : m;
          if (mBase > 0) row.discount_percentage = Math.max(0, Math.min(100, (1 - p / mBase) * 100)).toFixed(2);
        }
      }
      next[rowIdx] = row;
      return next;
    });
  };

  const removeRow = (rowIdx) => {
    setItems(prev => prev.length === 1 ? [emptyRow()] : prev.filter((_, i) => i !== rowIdx));
    cellRefs.current.splice(rowIdx, 1);
  };

  const addRow = () => setItems(prev => [...prev, emptyRow()]);

  // ── Quick-add: create Supplier / Location / Item without leaving the GRN ──
  const openQuickAdd = (kind, name = '', rowIdx = null) => setQuickAdd({ kind, name, rowIdx });
  const closeQuickAdd = () => setQuickAdd(null);

  const onSupplierCreated = (sup) => {
    setSuppliers(prev => [sup, ...prev.filter(s => s.id !== sup.id)]);
    setHeader(p => ({ ...p, irm_pharm_suppliers_id: String(sup.id) }));
    setTimeout(() => focusEl(locRef), 60);
  };
  const onLocationCreated = (loc) => {
    setLocations(prev => [loc, ...prev.filter(l => l.id !== loc.id)]);
    setHeader(p => ({ ...p, irm_pharm_store_locations_id: String(loc.id) }));
    setTimeout(() => focusEl(invNoRef), 60);
  };
  const onItemCreated = (item) => {
    const rowIdx = quickAdd?.rowIdx ?? 0;
    handleItemSelect(rowIdx, item);
  };

  // ── Global keyboard shortcuts (Tally-style) ──
  useEffect(() => {
    if (readOnly) return undefined;
    const onKey = (e) => {
      if (quickAddOpen) return;
      const ctrl = e.ctrlKey || e.metaKey;
      if (ctrl && (e.key === 's' || e.key === 'S')) { e.preventDefault(); saveDraftRef.current?.(); }
      else if (ctrl && e.key === 'Enter') { e.preventDefault(); confirmGrnRef.current?.(); }
      else if (e.altKey && (e.key === 'a' || e.key === 'A')) { e.preventDefault(); addRow(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [readOnly, quickAddOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleItemSelect = (rowIdx, selected) => {
    const subPer = selected.sub_units_per_base || 1;
    const baseUnit =
      selected.base_unit?.short_name || selected.base_unit?.name ||
      selected.base_unit_name || selected.unit?.short_name ||
      selected.unit?.name   || selected.unit_name || '';
    const subUnit  =
      selected.sub_unit?.short_name  || selected.sub_unit?.name  ||
      selected.sub_unit_name  || selected.piece_unit?.short_name ||
      selected.piece_unit?.name || '';
    // fallback: if no distinct sub unit configured, use base unit (1:1 ratio)
    const effectiveSubUnit = subUnit || baseUnit;
    const hsnCode =
      selected.hsn_code?.hsn_code || selected.hsn_code?.code ||
      (typeof selected.hsn_code === 'string' ? selected.hsn_code : '') ||
      selected.hsn?.hsn_code || selected.hsn_code_value || '';
    // auto-select default variant and pull its MRP if available
    const defaultVariant = (selected.variants || []).find(v => v.is_default === 'Y') || null;
    const variantMrp = defaultVariant?.mrp_per_base_unit ?? selected.mrp_per_base_unit;
    updateRow(rowIdx, {
      _itemData: { id: selected.id, name: selected.name, item_code: selected.item_code },
      _itemName: (selected.name || '').toUpperCase(),
      _variants: selected.variants || [],
      _variant_name: '',
      _sub_units_per_base: subPer,
      _base_unit: baseUnit,
      _sub_unit: effectiveSubUnit,
      _has_sub_unit: true, // always show; when subPer=1 sub-unit mirrors base qty
      _hsn_code: hsnCode,
      irm_pharm_items_id: String(selected.id),
      irm_pharm_item_variants_id: defaultVariant ? String(defaultVariant.id) : '',
      qty_units: '',
      qty_sub_units: '',
      mrp: (variantMrp !== null && variantMrp !== undefined) ? String(variantMrp) : '',
      gst_percentage: (() => {
        const g = selected.hsn_code?.gst_percentage ?? selected.hsn?.gst_percentage ?? selected.gst_percentage;
        return g !== null && g !== undefined ? String(g) : '';
      })(),
      discount_percentage: selected.discount_percentage ? String(selected.discount_percentage) : '0',
    });
    setTimeout(() => moveFocus(rowIdx, 0, 1), 50);
  };

  // ── Build payload ─────────────────────────────────────────
  const buildPayload = () => ({
    irm_pharm_pharmacies_id: selectedPharmacy?.id || null,
    irm_pharm_suppliers_id: parseInt(header.irm_pharm_suppliers_id) || null,
    irm_pharm_store_locations_id: parseInt(header.irm_pharm_store_locations_id) || null,
    supplier_invoice_no: header.supplier_invoice_no || null,
    supplier_invoice_date: header.supplier_invoice_date || null,
    received_date: header.received_date,
    notes: header.notes || null,
    round_off_enabled: header.round_off_enabled === 'N' ? 'N' : 'Y',
    items: items.filter(r => r.irm_pharm_items_id).map((r, i) => {
      const gst  = parseFloat(r.gst_percentage) || 0;
      const cgst = header._is_interstate ? 0 : gst / 2;
      const sgst = header._is_interstate ? 0 : gst / 2;
      const igst = header._is_interstate ? gst : 0;
      return {
        irm_pharm_items_id: parseInt(r.irm_pharm_items_id),
        irm_pharm_item_variants_id: r.irm_pharm_item_variants_id ? parseInt(r.irm_pharm_item_variants_id) : null,
        batch_no: r.batch_no.toUpperCase(),
        manufacturing_date: r.manufacturing_date ? `${r.manufacturing_date}-01` : null,
        expiry_date: r.expiry_date ? `${r.expiry_date}-01` : null,
        qty_received: parseFloat(r.qty_units) || 0,
        qty_free: 0,
        mrp: parseFloat(r.mrp) || 0,
        purchase_price: parseFloat(r.purchase_price) || 0,
        discount_percentage: parseFloat(r.discount_percentage) || 0,
        gst_percentage: gst, cgst_percentage: cgst, sgst_percentage: sgst, igst_percentage: igst,
        cess_percentage: parseFloat(r.cess_percentage) || 0,
        sort_order: i,
      };
    }),
  });

  // ── GRN actions ───────────────────────────────────────────
  const saveDraft = async () => {
    if (!header.irm_pharm_suppliers_id) { Swal.fire({ icon: 'warning', title: 'Select a supplier' }); return; }
    if (!header.irm_pharm_store_locations_id) { Swal.fire({ icon: 'warning', title: 'Select a location' }); return; }
    const payload = buildPayload();
    if (!payload.items.length) { Swal.fire({ icon: 'warning', title: 'Add at least one item' }); return; }
    if (payload.items.some(it => !it.manufacturing_date)) { Swal.fire({ icon: 'warning', title: 'Manufacturing date required', text: 'Enter the manufacturing month/year for every item.' }); return; }
    try {
      setSaving(true);
      if (isNew) {
        const res = await api.pharmacy.createInward(payload);
        await Swal.fire({ icon: 'success', title: 'Draft Saved', text: res.data?.inward_no, timer: 1500, showConfirmButton: false });
        navigate(`/pharmacy/inward/${res.data.id}`, { replace: true });
      } else {
        await api.pharmacy.updateInward(id, payload);
        await Swal.fire({ icon: 'success', title: 'Saved', timer: 1000, showConfirmButton: false });
        fetchInvoice();
      }
    } catch (e) { Swal.fire({ icon: 'error', title: 'Save Failed', text: e?.response?.data?.message || e.message }); }
    finally { setSaving(false); }
  };

  const confirmGrn = async () => {
    const { isConfirmed } = await Swal.fire({
      title: 'Confirm GRN?', html: 'Stock will be added to inventory.<br><b style="color:#d33">This cannot be undone.</b>',
      icon: 'question', showCancelButton: true, confirmButtonColor: TEAL, confirmButtonText: 'Confirm & Add Stock',
    });
    if (!isConfirmed) return;
    try {
      setSaving(true);
      await api.pharmacy.confirmInward(id);
      Swal.fire({ icon: 'success', title: 'GRN Confirmed!', text: 'Stock added to inventory.', timer: 2000, showConfirmButton: false });
      fetchInvoice();
    } catch (e) { Swal.fire({ icon: 'error', title: 'Error', text: e?.response?.data?.message || e.message }); }
    finally { setSaving(false); }
  };

  // keep the global-shortcut effect pointed at the current handlers
  saveDraftRef.current = () => { if (!saving) saveDraft(); };
  confirmGrnRef.current = () => { if (!saving && !isNew) confirmGrn(); };

  const cancelGrn = async () => {
    const { isConfirmed, value: reason } = await Swal.fire({
      title: 'Cancel GRN?', input: 'text', inputLabel: 'Reason for cancellation', inputPlaceholder: 'Enter reason…',
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Cancel GRN', cancelButtonText: 'Go Back',
    });
    if (!isConfirmed || !reason) return;
    try {
      setSaving(true);
      await api.pharmacy.cancelInward(id, { cancel_reason: reason });
      Swal.fire({ icon: 'success', title: 'GRN Cancelled', timer: 1500, showConfirmButton: false });
      fetchInvoice();
    } catch (e) { Swal.fire({ icon: 'error', title: 'Error', text: e?.response?.data?.message || e.message }); }
    finally { setSaving(false); }
  };

  // ── Document handlers ─────────────────────────────────────
  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingDoc(true);
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', docType);
      await api.pharmacy.uploadInwardDocument(id, fd);
      await loadDocuments();
      e.target.value = '';
    } catch { /* error shown by ApiClient */ }
    finally { setUploadingDoc(false); }
  };

  const handleDocDelete = async (docId) => {
    const { isConfirmed } = await Swal.fire({ title: 'Delete this document?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Delete' });
    if (!isConfirmed) return;
    try {
      await api.pharmacy.deleteInwardDocument(id, docId);
      setGrnDocs(prev => prev.filter(d => d.id !== docId));
    } catch { /* error shown by ApiClient */ }
  };

  // ── Timeline handler ──────────────────────────────────────
  const openTimeline = async () => {
    setShowTimeline(true);
    setTimelineLoading(true);
    try {
      const res = await api.pharmacy.getInwardTimeline(id);
      setTimelineEvents(res?.data || []);
    } catch { setTimelineEvents([]); }
    finally { setTimelineLoading(false); }
  };

  // ── Settlement handler ────────────────────────────────────
  const settleInvoice = async () => {
    const settlementThreshold = Math.min(amountDue * 0.005, 100);
    const { isConfirmed, value: notes } = await Swal.fire({
      title: 'Settle & Close Invoice?',
      html: `<div style="text-align:left;font-size:0.88rem">
        <p>A small outstanding amount of <b>₹${outstanding.toFixed(2)}</b> will be written off as a payment difference.</p>
        <p style="color:#666;margin-top:6px">Settlement limit: ₹${settlementThreshold.toFixed(2)} (min of 0.5% or ₹100)</p>
      </div>`,
      input: 'text', inputLabel: 'Settlement notes (optional)', inputPlaceholder: 'e.g. TDS deducted, rounding off…',
      icon: 'question', showCancelButton: true,
      confirmButtonColor: '#2e7d32', confirmButtonText: 'Settle & Close', cancelButtonText: 'Go Back',
    });
    if (!isConfirmed) return;
    try {
      setSaving(true);
      await api.pharmacy.settleInward(id, { notes: notes || 'Payment difference settled' });
      Swal.fire({ icon: 'success', title: 'Invoice Settled & Closed!', timer: 2000, showConfirmButton: false });
      fetchInvoice();
    } catch (e) { Swal.fire({ icon: 'error', title: 'Error', text: e?.response?.data?.message || e.message }); }
    finally { setSaving(false); }
  };

  // ── GRN Return handlers ───────────────────────────────────
  const openReturnDialog = async () => {
    try {
      const res = await api.pharmacy.listInwardReturns(id);
      const rets = res.data || [];
      setExistingReturns(rets);
      setReturnLines(
        invoice.items
          .filter(it => it.irm_pharm_items_id)
          .map(it => {
            const alreadyReturned = rets
              .filter(r => r.status !== 'CANCELLED')
              .flatMap(r => r.items || [])
              .filter(ri => ri.irm_pharm_inward_invoice_items_id === it.id)
              .reduce((s, ri) => s + Number(ri.qty_returned), 0);
            return {
              id: it.id,
              name: it.item?.name || '',
              batch_no: it.batch_no || '',
              qty_received: Number(it.qty_received || 0),
              already_returned: alreadyReturned,
              available: Math.max(0, Number(it.qty_received || 0) - alreadyReturned),
              qty_return: '',
            };
          })
      );
      setReturnReason('');
      setReturnDate(new Date().toISOString().substring(0, 10));
      setShowReturnDialog(true);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e?.response?.data?.message || e.message });
    }
  };

  const submitReturn = async () => {
    const lines = returnLines.filter(l => parseFloat(l.qty_return) > 0);
    if (!lines.length) { Swal.fire({ icon: 'warning', title: 'Enter at least one return quantity' }); return; }
    if (!returnReason.trim()) { Swal.fire({ icon: 'warning', title: 'Reason is required' }); return; }
    for (const l of lines) {
      if (parseFloat(l.qty_return) > l.available) {
        Swal.fire({ icon: 'warning', title: `${l.name || 'Item'}: return qty exceeds available (${l.available})` });
        return;
      }
    }
    try {
      setSavingReturn(true);
      await api.pharmacy.createInwardReturn(id, {
        return_date: returnDate,
        reason: returnReason.trim(),
        items: lines.map(l => ({ irm_pharm_inward_invoice_items_id: l.id, qty_returned: parseFloat(l.qty_return) })),
      });
      Swal.fire({ icon: 'success', title: 'Return Processed!', timer: 1800, showConfirmButton: false });
      setShowReturnDialog(false);
      fetchInvoice();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e?.response?.data?.message || e.message });
    } finally {
      setSavingReturn(false);
    }
  };

  // ── Payment handlers ──────────────────────────────────────
  const openAddPayment = () => { setEditingTxnId(null); setPaymentForm(emptyPaymentForm()); setPodFile(null); setShowPaymentForm(true); };
  const openEditPayment = (txn) => {
    setEditingTxnId(txn.id);
    setPaymentForm({ amount: String(txn.amount || ''), payment_mode: txn.payment_mode || '', payment_date: txn.payment_date?.substring(0, 10) || '', utr_number: txn.utr_number || '', notes: txn.notes || '' });
    setPodFile(null);
    setShowPaymentForm(true);
  };
  const closePaymentForm = () => { setShowPaymentForm(false); setEditingTxnId(null); setPaymentForm(emptyPaymentForm()); setPodFile(null); };

  const savePaymentTxn = async () => {
    if (!paymentForm.amount || !paymentForm.payment_mode) { Swal.fire({ icon: 'warning', title: 'Amount and Payment Mode are required' }); return; }
    try {
      setSavingPayment(true);
      const payload = { amount: parseFloat(paymentForm.amount), payment_mode: paymentForm.payment_mode, payment_date: paymentForm.payment_date || null, utr_number: paymentForm.utr_number || null, notes: paymentForm.notes || null };
      let savedTxn;
      if (editingTxnId && editingTxnId !== 'legacy') {
        const res = await api.pharmacy.updatePaymentTransaction(id, editingTxnId, payload);
        savedTxn = res?.data;
      } else {
        const res = await api.pharmacy.addPaymentTransaction(id, payload);
        savedTxn = res?.data;
        if (podFile) { const fd = new FormData(); fd.append('file', podFile); await api.pharmacy.uploadPaymentPod(id, fd).catch(() => {}); }
      }
      Swal.fire({ icon: 'success', title: editingTxnId ? 'Payment Updated' : 'Payment Added', timer: 1000, showConfirmButton: false });
      closePaymentForm();
      fetchInvoice();
      if (savedTxn) {
        const [invRes, txnsRes] = await Promise.all([
          api.pharmacy.getInward(id),
          api.pharmacy.listPaymentTransactions(id),
        ]);
        printSupplierPayment(savedTxn, invRes?.data, txnsRes?.data || [], selectedPharmacy);
      }
    } catch (e) { Swal.fire({ icon: 'error', title: 'Error', text: e?.response?.data?.message || e.message }); }
    finally { setSavingPayment(false); }
  };

  const deletePaymentTxn = async (txn) => {
    const { isConfirmed } = await Swal.fire({ title: 'Delete this payment?', text: `${fmt(txn.amount)} via ${txn.payment_mode}`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Delete' });
    if (!isConfirmed) return;
    try {
      if (txn._legacy || txn.id === 'legacy') {
        await api.pharmacy.updateInwardPayment(id, { amount_paid: 0, payment_status: 'PENDING' });
      } else {
        await api.pharmacy.deletePaymentTransaction(id, txn.id);
      }
      Swal.fire({ icon: 'success', title: 'Payment Deleted', timer: 1000, showConfirmButton: false });
      fetchInvoice();
    } catch (e) { Swal.fire({ icon: 'error', title: 'Error', text: e?.response?.data?.message || e.message }); }
  };

  const finalizeInvoice = async () => {
    const { isConfirmed } = await Swal.fire({ title: 'Finalize Invoice?', html: 'Marks the invoice as fully settled and closes it.<br><b>This cannot be undone.</b>', icon: 'question', showCancelButton: true, confirmButtonColor: TEAL, confirmButtonText: 'Finalize & Close' });
    if (!isConfirmed) return;
    try {
      await api.pharmacy.finalizeInward(id);
      Swal.fire({ icon: 'success', title: 'Invoice Finalized!', timer: 2000, showConfirmButton: false });
      fetchInvoice();
    } catch (e) { Swal.fire({ icon: 'error', title: 'Error', text: e?.response?.data?.message || e.message }); }
  };

  // ── Computed values ───────────────────────────────────────
  const totalPaid   = paymentTxns.filter(t => t.status !== 'CANCELLED').reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
  const synopsis    = calcPriceSynopsis(items, header._is_interstate, header.round_off_enabled !== 'N');
  const amountDue   = parseFloat(paymentSummary?.amount_due) || synopsis.netPayable;
  const outstanding = Math.max(0, amountDue - totalPaid);
  const isFullyPaid = outstanding < 0.01 && paymentTxns.filter(t => t.status !== 'CANCELLED').length > 0;
  const settlementThreshold = Math.min(amountDue * 0.005, 100);
  const isSettleable = outstanding > 0.01 && outstanding <= settlementThreshold && paymentTxns.filter(t => t.status !== 'CANCELLED').length > 0;

  if (loadingInv) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress sx={{ color: TEAL }} />
        </Box>
        <Footer />
      </DashboardLayout>
    );
  }

  // ── Render ────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {saving && <LinearProgress sx={{ mb: 1, bgcolor: '#e0f2f4', '& .MuiLinearProgress-bar': { bgcolor: TEAL } }} />}

        {!selectedPharmacy && (
          <Alert severity="warning" sx={{ mb: 2, fontSize: '0.85rem' }}>
            No pharmacy selected. Please select a pharmacy from the ribbon before saving.
          </Alert>
        )}

        {!readOnly && (
          <KeyboardHintBar storageKey="pharm-grn">
            <b>Ctrl+S</b> Save Draft · <b>Ctrl+↵</b> Confirm GRN · <b>Alt+A</b> Add row · <b>↵</b> next field · <b>Tab / ↵</b> next cell · in Supplier/Location/Material type a new name → <b>↓ ↵</b> to create it here
          </KeyboardHintBar>
        )}

        {/* ── Top action bar ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <IconButton size="small" onClick={() => navigate('/pharmacy/inward')}>
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <MDTypography variant="h5" fontWeight="bold" sx={{ mr: 1 }}>
            {isNew ? 'New Purchase Inward' : invoice?.inward_no || `GRN #${id}`}
          </MDTypography>
          {invoice && (
            <Chip label={(invoice.status || '').replace('_', ' ')} size="small"
              color={STATUS_COLOR[invoice.status] || 'default'} sx={{ fontWeight: 700, fontSize: '0.68rem' }} />
          )}
          <Box sx={{ flexGrow: 1 }} />
          {/* Documents button — always visible for saved GRNs */}
          {!isNew && (
            <Button variant="outlined" size="small" startIcon={<FolderOpenIcon />}
              sx={{ textTransform: 'none', color: '#5c6bc0 !important', borderColor: '#5c6bc0', '&:hover': { bgcolor: '#f3f4ff' } }}
              onClick={() => setShowDocsDialog(true)}>
              Files{grnDocs.length > 0 ? ` (${grnDocs.length})` : ''}
            </Button>
          )}
          {/* Timeline button */}
          {!isNew && (
            <Button variant="outlined" size="small" startIcon={<TimelineIcon />}
              sx={{ textTransform: 'none', color: '#1a237e !important', borderColor: '#3949ab', '&:hover': { bgcolor: '#e8eaf6' } }}
              onClick={openTimeline}>
              Timeline
            </Button>
          )}
          {/* Print GRN */}
          {!isNew && invoice && (
            <Button variant="outlined" size="small" startIcon={<PrintIcon />}
              sx={{ textTransform: 'none', color: `${TEAL} !important`, borderColor: TEAL, '&:hover': { bgcolor: `${TEAL}08` } }}
              onClick={() => printGrn(invoice, items, selectedPharmacy)}>
              Print GRN
            </Button>
          )}
          {/* Cancel available for DRAFT and for RECEIVED/PARTIALLY_RETURNED when not fully paid */}
          {invoice && invoice.status !== 'CANCELLED' && paymentSummary?.payment_status !== 'PAID' && (
            <Button variant="outlined" size="small" startIcon={<CancelOutlinedIcon />}
              sx={{ textTransform: 'none', color: '#d33 !important', borderColor: '#d33', '&:hover': { bgcolor: '#fff5f5' } }}
              onClick={cancelGrn} disabled={saving || isNew}>
              Cancel GRN
            </Button>
          )}
          {/* Return Items — only for RECEIVED or PARTIALLY_RETURNED */}
          {(invoice?.status === 'RECEIVED' || invoice?.status === 'PARTIALLY_RETURNED') && (
            <Button variant="outlined" size="small" startIcon={<DeleteOutlineIcon />}
              sx={{ textTransform: 'none', color: '#e65100 !important', borderColor: '#e65100', '&:hover': { bgcolor: '#fff3e0' } }}
              onClick={openReturnDialog} disabled={saving}>
              Return Items
            </Button>
          )}
          {!readOnly && (
            <>
              <Button variant="outlined" size="small" startIcon={<SaveIcon />}
                sx={{ textTransform: 'none', color: `${TEAL} !important`, borderColor: TEAL, '&:hover': { bgcolor: `${TEAL}08` } }}
                onClick={saveDraft} disabled={saving}>
                Save Draft
              </Button>
              <Button variant="contained" size="small" startIcon={<CheckCircleOutlineIcon />}
                sx={{ bgcolor: TEAL, color: 'white !important', '&:hover': { bgcolor: '#005e6e', color: 'white !important' }, textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
                onClick={confirmGrn} disabled={saving || isNew}>
                Confirm GRN
              </Button>
            </>
          )}
        </Box>

        {readOnly && invoice?.status !== 'RECEIVED' && invoice?.status !== 'PARTIALLY_RETURNED' && (
          <Alert severity="info" sx={{ mb: 2, fontSize: '0.82rem' }}>
            This GRN is {invoice?.status?.replace('_', ' ')} and cannot be edited.
          </Alert>
        )}

        {/* ── Header card ── */}
        <Card sx={{ mb: 2, p: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FieldLabel label="Supplier *">
                <Box ref={supRef}>
                  <CreatableSelect options={suppliers.map(s => ({ value: String(s.id), label: s.name }))}
                    value={header.irm_pharm_suppliers_id}
                    onChange={(v) => setHeader(p => ({ ...p, irm_pharm_suppliers_id: v }))}
                    onCreateNew={readOnly ? undefined : (txt) => openQuickAdd('supplier', txt)}
                    disabled={readOnly} placeholder="Select / create supplier"
                    createLabel="Create supplier"
                    onKeyDownLast={hdrEnter(locRef)} />
                </Box>
              </FieldLabel>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FieldLabel label="Location *">
                <Box ref={locRef}>
                  <CreatableSelect options={locations.map(l => ({ value: String(l.id), label: l.name }))}
                    value={header.irm_pharm_store_locations_id}
                    onChange={(v) => setHeader(p => ({ ...p, irm_pharm_store_locations_id: v }))}
                    onCreateNew={readOnly ? undefined : (txt) => openQuickAdd('location', txt)}
                    disabled={readOnly} placeholder="Select / create location"
                    createLabel="Create location"
                    onKeyDownLast={hdrEnter(invNoRef)} />
                </Box>
              </FieldLabel>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FieldLabel label="Supplier Invoice No">
                <TextField size="small" fullWidth inputRef={invNoRef} value={header.supplier_invoice_no || ''}
                  onChange={(e) => setHeader(p => ({ ...p, supplier_invoice_no: e.target.value.toUpperCase() }))}
                  onKeyDown={hdrEnter(invDateRef)}
                  disabled={readOnly}
                  sx={{ '& .MuiOutlinedInput-root': { height: 34, fontSize: '0.82rem' } }} />
              </FieldLabel>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FieldLabel label="Supplier Invoice Date">
                <Box ref={invDateRef} onKeyDown={hdrEnter(recvDateRef)}>
                  <MDDatePicker value={header.supplier_invoice_date || null}
                    onChange={(v) => setHeader(p => ({ ...p, supplier_invoice_date: v || '' }))} disabled={readOnly} />
                </Box>
              </FieldLabel>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FieldLabel label="Received Date *">
                <Box ref={recvDateRef} onKeyDown={hdrEnter(null, true)}>
                  <MDDatePicker value={header.received_date || null}
                    onChange={(v) => setHeader(p => ({ ...p, received_date: v || '' }))} disabled={readOnly} />
                </Box>
              </FieldLabel>
            </Grid>
            <Grid item xs={12} sm={8} md={9}>
              <FieldLabel label="Notes">
                <Box sx={{ '& .ql-container': { fontSize: '0.82rem', minHeight: 64, maxHeight: 130 }, '& .ql-toolbar': { py: 0.25, '& button': { height: 20, width: 20 }, '& .ql-formats': { mr: 0.5 } }, opacity: readOnly ? 0.55 : 1, pointerEvents: readOnly ? 'none' : 'auto' }}>
                  <ReactQuill theme="snow" value={header.notes || ''}
                    onChange={(val) => setHeader(p => ({ ...p, notes: val }))}
                    modules={{ toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean']] }} />
                </Box>
              </FieldLabel>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Box sx={{ mt: 2.5 }}>
                <FormControlLabel
                  control={<Switch size="small" checked={header._is_interstate || false}
                    onChange={(e) => setHeader(p => ({ ...p, _is_interstate: e.target.checked }))}
                    disabled={readOnly}
                    sx={{ '& .MuiSwitch-thumb': { bgcolor: header._is_interstate ? TEAL : undefined } }} />}
                  label={<Typography sx={{ fontSize: '0.78rem', color: '#555' }}>Interstate Supply (IGST)</Typography>}
                />
              </Box>
            </Grid>
          </Grid>
        </Card>

        {/* ── Items — page-level flat table (no card) ── */}
        <Box sx={{ mb: 2 }}>
          {/* <SectionHeader title="Items" /> */}
          <TableContainer sx={{ overflowX: 'auto', bgcolor: '#fff' }}>
            <Table size="small" sx={{ minWidth: 1680, tableLayout: 'fixed' }}>
              {/* Header row */}
              <TableRow>
                <TableCell sx={{ ...TH, width: 30 }}>#</TableCell>
                <TableCell sx={{ ...TH, width: 195 }}>Item</TableCell>
                <TableCell sx={{ ...TH, width: 95 }}>Variant</TableCell>
                <TableCell sx={{ ...TH, width: 80 }}>Batch</TableCell>
                <TableCell sx={{ ...TH, width: 82 }}>Mfg Date</TableCell>
                <TableCell sx={{ ...TH, width: 82 }}>Expiry</TableCell>
                <TableCell sx={{ ...TH, width: 110 }}>Units</TableCell>
                <TableCell sx={{ ...TH, width: 110 }}>Sub-Units</TableCell>
                <TableCell sx={{ ...TH, width: 76 }}>MRP/Unit</TableCell>
                <TableCell sx={{ ...TH, width: 72 }}>
                  MRP/Sub
                  <Box component="span" sx={{ display: 'block', fontSize: '0.5rem', opacity: 0.75, textTransform: 'none', fontWeight: 400 }}>auto</Box>
                </TableCell>
                <TableCell sx={{ ...TH, width: 74 }}>
                  Price/Unit
                  <Box component="span" sx={{ display: 'block', fontSize: '0.5rem', opacity: 0.75, textTransform: 'none', fontWeight: 400 }}>ex-GST</Box>
                </TableCell>
                <TableCell sx={{ ...TH, width: 72 }}>
                  Price/Sub
                  <Box component="span" sx={{ display: 'block', fontSize: '0.5rem', opacity: 0.75, textTransform: 'none', fontWeight: 400 }}>ex-GST · auto</Box>
                </TableCell>
                <TableCell sx={{ ...TH, width: 58 }}>
                  Disc %
                  <Box component="span" sx={{ display: 'block', fontSize: '0.5rem', opacity: 0.75, textTransform: 'none', fontWeight: 400 }}>auto</Box>
                </TableCell>
                <TableCell sx={{ ...TH, width: 82 }}>HSN Code</TableCell>
                <TableCell sx={{ ...TH, width: 58 }}>
                  GST %
                  <Box component="span" sx={{ display: 'block', fontSize: '0.5rem', opacity: 0.75, textTransform: 'none', fontWeight: 400 }}>from HSN</Box>
                </TableCell>
                <TableCell sx={{ ...TH, width: 50 }}>Cess %</TableCell>
                <TableCell sx={{ ...TH, width: 88, textAlign: 'right' }}>Line Total</TableCell>
                <TableCell sx={{ ...TH, width: 28 }} />
                <TableCell sx={{ ...TH, width: 28 }} />
              </TableRow>

              {/* Data rows */}
              <TableBody>
                {items.map((row, ri) => {
                  const lineTotal    = calcLineTotal(row, header._is_interstate);
                  const variantLabel = row._variants.find(v => String(v.id) === row.irm_pharm_item_variants_id)?.variant_name
                    || row._variant_name
                    || null;

                  return (
                    <TableRow key={row._key} hover sx={{ '&:hover': { bgcolor: '#fafeff' } }}>
                      {/* # */}
                      <TableCell sx={{ ...TD, textAlign: 'center', fontSize: '0.68rem', color: '#bbb' }}>{ri + 1}</TableCell>

                      {/* Item */}
                      <TableCell sx={TD}>
                        <ItemCell row={row} onSelect={(s) => handleItemSelect(ri, s)} readOnly={readOnly}
                          cellRef={makeCellRef(ri, 0)} onTabKey={(dir) => moveFocus(ri, 0, dir)}
                          onCreateNew={(txt) => openQuickAdd('item', txt, ri)} />
                      </TableCell>

                      {/* Variant */}
                      <TableCell sx={TD}>
                        {readOnly ? (
                          <Box sx={{ px: 0.75, lineHeight: '30px', fontSize: '0.75rem', color: variantLabel ? '#333' : '#ccc' }}>
                            {variantLabel || (row.irm_pharm_item_variants_id ? `#${row.irm_pharm_item_variants_id}` : '—')}
                          </Box>
                        ) : row._variants?.length > 0 ? (
                          <Select size="small" fullWidth displayEmpty value={row.irm_pharm_item_variants_id || ''}
                            onChange={(e) => {
                              const variantId = e.target.value;
                              const v = row._variants.find(vr => String(vr.id) === String(variantId));
                              const patch = { irm_pharm_item_variants_id: variantId };
                              if (v?.mrp_per_base_unit !== null && v?.mrp_per_base_unit !== undefined) patch.mrp = String(v.mrp_per_base_unit);
                              if (v?.sub_units_per_base !== null && v?.sub_units_per_base !== undefined && v.sub_units_per_base > 0)
                                patch._sub_units_per_base = v.sub_units_per_base;
                              updateRow(ri, patch);
                            }}
                            disabled={readOnly}
                            tabIndex={-1}
                            sx={{ height: 30, fontSize: '0.76rem', borderRadius: 0, '& fieldset': { border: '1px solid transparent' }, '&:hover fieldset': { borderColor: '#ddd' } }}
                            inputProps={{ tabIndex: -1 }}>
                            <MenuItem value=""><em>None</em></MenuItem>
                            {row._variants.map(v => <MenuItem key={v.id} value={String(v.id)}>{v.variant_name}</MenuItem>)}
                          </Select>
                        ) : (
                          <Box sx={{ px: 0.75, fontSize: '0.7rem', color: '#ccc', lineHeight: '30px' }}>—</Box>
                        )}
                      </TableCell>

                      {/* Batch */}
                      <TableCell sx={TD}>
                        <GridNumCell value={row.batch_no} align="left"
                          onChange={(e) => updateRow(ri, { batch_no: e.target.value.toUpperCase() })}
                          disabled={readOnly} inputRef={makeCellRef(ri, 2)} onKeyDown={cellKeyDown(ri, 2)} />
                      </TableCell>

                      {/* Manufacturing Date */}
                      <TableCell sx={TD}>
                        {readOnly ? (
                          <Typography sx={{ fontSize: '0.78rem', fontFamily: 'monospace', px: 0.5 }}>{fmtMonth(row.manufacturing_date)}</Typography>
                        ) : (
                          <MDMonthYearPicker
                            value={row.manufacturing_date}
                            onChange={(v) => updateRow(ri, { manufacturing_date: v || '' })}
                            inputRef={makeCellRef(ri, 3)} onKeyDown={cellKeyDown(ri, 3)}
                            sx={gridDateInputSx()} inputProps={{ tabIndex: -1 }} />
                        )}
                      </TableCell>

                      {/* Expiry */}
                      <TableCell sx={TD}>
                        {readOnly ? (
                          <Typography sx={{ fontSize: '0.78rem', fontFamily: 'monospace', px: 0.5 }}>{fmtExpiry(row.expiry_date)}</Typography>
                        ) : (
                          <MDMonthYearPicker
                            value={row.expiry_date}
                            onChange={(v) => updateRow(ri, { expiry_date: v || '' })}
                            inputRef={makeCellRef(ri, 4)} onKeyDown={cellKeyDown(ri, 4)}
                            sx={gridDateInputSx()} inputProps={{ tabIndex: -1 }} />
                        )}
                      </TableCell>

                      {/* Units — UnitInput micro-component */}
                      <TableCell sx={{ ...TD, p: 0 }}>
                        <UnitInput
                          value={row.qty_units}
                          unitName={row._base_unit}
                          disabled={readOnly}
                          inputRef={makeCellRef(ri, 5)}
                          onKeyDown={cellKeyDown(ri, 5)}
                          onChange={(e) => {
                            const v = e.target.value;
                            const sub = row._has_sub_unit && v !== '' ? String(Math.round(parseFloat(v) * row._sub_units_per_base)) : row.qty_sub_units;
                            updateRow(ri, { qty_units: v, qty_sub_units: sub });
                          }}
                        />
                      </TableCell>

                      {/* Sub-Units — always visible; mirrors base qty when subPer≤1 */}
                      <TableCell sx={{ ...TD, p: 0 }}>
                        {row._itemData ? (
                          <UnitInput
                            value={row._sub_units_per_base > 1 ? row.qty_sub_units : row.qty_units}
                            unitName={row._sub_unit || row._base_unit}
                            disabled={readOnly || row._sub_units_per_base <= 1}
                            inputRef={row._sub_units_per_base > 1 ? makeCellRef(ri, 6) : undefined}
                            onKeyDown={row._sub_units_per_base > 1 ? cellKeyDown(ri, 6) : undefined}
                            onChange={(e) => {
                              if (row._sub_units_per_base <= 1) return;
                              const v = e.target.value;
                              const units = v !== '' ? String((parseFloat(v) / row._sub_units_per_base).toFixed(4)).replace(/\.?0+$/, '') : row.qty_units;
                              updateRow(ri, { qty_sub_units: v, qty_units: units });
                            }}
                          />
                        ) : (
                          <Box sx={{ px: 0.75, fontSize: '0.7rem', color: '#ccc', lineHeight: '30px', textAlign: 'right' }}>—</Box>
                        )}
                      </TableCell>

                      {/* MRP/Unit */}
                      <TableCell sx={{ ...TD, p: 0 }}>
                        <GridNumCell value={row.mrp}
                          onChange={(e) => updateRow(ri, { mrp: e.target.value })}
                          onBlur={(e) => { const r = round2(e.target.value); if (r !== row.mrp) updateRow(ri, { mrp: r }); }}
                          disabled={readOnly} inputRef={makeCellRef(ri, 7)} onKeyDown={cellKeyDown(ri, 7)} />
                      </TableCell>

                      {/* MRP/Sub-Unit — auto-calculated, read-only */}
                      <TableCell sx={{ ...TD, px: 0.75, textAlign: 'right' }}>
                        {row._itemData && parseFloat(row.mrp) > 0 ? (
                          <Typography sx={{ fontSize: '0.76rem', fontFamily: 'monospace', color: row._sub_units_per_base > 1 ? '#444' : '#bbb', lineHeight: '30px' }}>
                            ₹{(parseFloat(row.mrp) / row._sub_units_per_base).toFixed(2)}
                          </Typography>
                        ) : (
                          <Typography sx={{ fontSize: '0.7rem', color: '#ddd', lineHeight: '30px' }}>—</Typography>
                        )}
                      </TableCell>

                      {/* Price/Unit (ex-GST) */}
                      <TableCell sx={{ ...TD, p: 0 }}>
                        <GridNumCell value={row.purchase_price}
                          onChange={(e) => updateRow(ri, { purchase_price: e.target.value })}
                          onBlur={(e) => { const r = round2(e.target.value); if (r !== row.purchase_price) updateRow(ri, { purchase_price: r }); }}
                          disabled={readOnly} inputRef={makeCellRef(ri, 8)} onKeyDown={cellKeyDown(ri, 8)} />
                      </TableCell>

                      {/* Price/Sub-Unit (ex-GST) — auto-calculated, read-only */}
                      <TableCell sx={{ ...TD, px: 0.75, textAlign: 'right' }}>
                        {row._itemData && parseFloat(row.purchase_price) > 0 ? (
                          <Typography sx={{ fontSize: '0.76rem', fontFamily: 'monospace', color: row._sub_units_per_base > 1 ? '#444' : '#bbb', lineHeight: '30px' }}>
                            ₹{(parseFloat(row.purchase_price) / row._sub_units_per_base).toFixed(2)}
                          </Typography>
                        ) : (
                          <Typography sx={{ fontSize: '0.7rem', color: '#ddd', lineHeight: '30px' }}>—</Typography>
                        )}
                      </TableCell>

                      {/* Discount % */}
                      <TableCell sx={{ ...TD, p: 0 }}>
                        <GridNumCell value={row.discount_percentage}
                          onChange={(e) => updateRow(ri, { discount_percentage: e.target.value })}
                          onBlur={(e) => { const r = round2(e.target.value); if (r !== row.discount_percentage) updateRow(ri, { discount_percentage: r }); }}
                          disabled={readOnly} inputRef={makeCellRef(ri, 9)} onKeyDown={cellKeyDown(ri, 9)} />
                      </TableCell>

                      {/* HSN Code — display-only, auto-filled */}
                      <TableCell sx={{ ...TD, px: 0.75 }}>
                        <Typography sx={{ fontSize: '0.7rem', fontFamily: 'monospace', color: row._hsn_code ? TEAL : '#ccc', fontWeight: row._hsn_code ? 600 : 400, lineHeight: '30px' }}>
                          {row._hsn_code || '—'}
                        </Typography>
                      </TableCell>

                      {/* GST % badge */}
                      <TableCell sx={{ ...TD, px: 0.75 }}>
                        <GstBadge value={row.gst_percentage} />
                      </TableCell>

                      {/* Cess % */}
                      <TableCell sx={{ ...TD, p: 0 }}>
                        <GridNumCell value={row.cess_percentage}
                          onChange={(e) => updateRow(ri, { cess_percentage: e.target.value })}
                          disabled={readOnly} inputRef={makeCellRef(ri, 10)} onKeyDown={cellKeyDown(ri, 10)} />
                      </TableCell>

                      {/* Line Total */}
                      <TableCell sx={{ ...TD, textAlign: 'right', pr: 1.5, fontWeight: 600, fontSize: '0.8rem', color: row.irm_pharm_items_id ? '#222' : '#ddd' }}>
                        {row.irm_pharm_items_id ? fmt(lineTotal) : '—'}
                      </TableCell>

                      {/* Info button */}
                      <TableCell sx={{ ...TD, px: 0.25 }}>
                        {row.irm_pharm_items_id && (
                          <Tooltip title="Price breakdown">
                            <IconButton size="small" onClick={() => setPriceDialogRow(row)}
                              sx={{ color: TEAL, opacity: 0.7, '&:hover': { opacity: 1 } }}>
                              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>

                      {/* Delete row */}
                      <TableCell sx={{ ...TD, px: 0.25 }}>
                        {!readOnly && (
                          <Tooltip title="Remove row">
                            <IconButton size="small" onClick={() => removeRow(ri)} sx={{ color: '#ccc', '&:hover': { color: '#d33' } }}>
                              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Add row footer bar */}
          {!readOnly && (
            <Box sx={{ px: 2, py: 1, borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', bgcolor: '#fff' }}>
              <Button size="small" startIcon={<AddIcon />} onClick={addRow}
                sx={{ textTransform: 'none', color: `${TEAL} !important`, fontWeight: 600, fontSize: '0.78rem', '&:hover': { bgcolor: TEAL_LIGHT } }}>
                Add Row
              </Button>
            </Box>
          )}
        </Box>

        {/* ── Price Synopsis card ── */}
        {synopsis.netPayable > 0 && (
          <Card sx={{ mb: 2, overflow: 'hidden' }}>
            <SectionHeader title="Price Summary" />
            <Box sx={{ p: 2.5 }}>

              {/* GST Rate-wise breakdown table */}
              {synopsis.rates.length > 0 && (
                <Box sx={{ mb: 2.5 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
                    GST Rate-wise Breakdown
                  </Typography>
                  <Box sx={{ border: `1px solid ${TEAL}22`, borderRadius: 1.5, overflow: 'hidden' }}>
                    <Table size="small">
                      <TableRow>
                        {['GST Rate', 'Taxable', ...(header._is_interstate ? ['IGST'] : ['CGST', 'SGST']), ...(synopsis.totalCess > 0 ? ['Cess'] : []), 'Tax Total', 'Line Total']
                          .map((h, i) => <TableCell key={i} sx={{ ...TH, textAlign: i > 0 ? 'right' : 'left' }}>{h}</TableCell>)}
                      </TableRow>
                      <TableBody>
                        {synopsis.rates.map((r) => (
                          <TableRow key={r.gst} hover>
                            <TableCell sx={{ py: 0.75, px: 1.5 }}>
                              <Chip label={`${r.gst}%`} size="small" sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }} />
                              {!header._is_interstate && r.gst > 0 && (
                                <Typography sx={{ fontSize: '0.55rem', color: '#aaa', mt: 0.2 }}>CGST {r.cgstRate}% + SGST {r.sgstRate}%</Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 0.75, px: 1.5, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem' }}>{fmt(r.taxable)}</TableCell>
                            {header._is_interstate
                              ? <TableCell sx={{ py: 0.75, px: 1.5, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem', color: '#1565c0' }}>{fmt(r.igst)}</TableCell>
                              : <>
                                  <TableCell sx={{ py: 0.75, px: 1.5, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem', color: '#1565c0' }}>{fmt(r.cgst)}</TableCell>
                                  <TableCell sx={{ py: 0.75, px: 1.5, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem', color: '#1565c0' }}>{fmt(r.sgst)}</TableCell>
                                </>
                            }
                            {synopsis.totalCess > 0 && <TableCell sx={{ py: 0.75, px: 1.5, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem', color: '#6a1b9a' }}>{fmt(r.cess)}</TableCell>}
                            <TableCell sx={{ py: 0.75, px: 1.5, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 700, color: '#444' }}>{fmt(r.gstTotal + r.cess)}</TableCell>
                            <TableCell sx={{ py: 0.75, px: 1.5, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 700, color: TEAL }}>{fmt(r.lineTotal)}</TableCell>
                          </TableRow>
                        ))}
                        {synopsis.rates.length > 1 && (
                          <TableRow sx={{ bgcolor: `${TEAL}06` }}>
                            <TableCell sx={{ py: 0.75, px: 1.5, fontWeight: 700, fontSize: '0.75rem', color: '#444' }}>Total</TableCell>
                            <TableCell sx={{ py: 0.75, px: 1.5, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 700 }}>{fmt(synopsis.totalTaxable)}</TableCell>
                            {header._is_interstate
                              ? <TableCell sx={{ py: 0.75, px: 1.5, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 700, color: '#1565c0' }}>{fmt(synopsis.totalIgst)}</TableCell>
                              : <>
                                  <TableCell sx={{ py: 0.75, px: 1.5, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 700, color: '#1565c0' }}>{fmt(synopsis.totalCgst)}</TableCell>
                                  <TableCell sx={{ py: 0.75, px: 1.5, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 700, color: '#1565c0' }}>{fmt(synopsis.totalSgst)}</TableCell>
                                </>
                            }
                            {synopsis.totalCess > 0 && <TableCell sx={{ py: 0.75, px: 1.5, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 700, color: '#6a1b9a' }}>{fmt(synopsis.totalCess)}</TableCell>}
                            <TableCell sx={{ py: 0.75, px: 1.5, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 700 }}>{fmt(synopsis.totalGst + synopsis.totalCess)}</TableCell>
                            <TableCell sx={{ py: 0.75, px: 1.5, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 800, color: TEAL }}>{fmt(synopsis.rawNetPayable)}</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              )}

              {/* Final totals */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 1.5, bgcolor: '#fafbfc' }}>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>Value Summary</Typography>
                    <SynRow label="Total Value (Qty × Purchase Price)" val={fmt(synopsis.grossValue)} />
                    {synopsis.totalDiscount > 0.005 && (
                      <SynRow label="Discount vs MRP (info — not deducted)" val={fmt(synopsis.totalDiscount)} color="#888" />
                    )}
                    <SynRow label="Taxable Value" val={fmt(synopsis.totalTaxable)} color="#1565c0" bold />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, border: `1px solid ${TEAL}30`, borderRadius: 1.5, bgcolor: `${TEAL}04` }}>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>Tax &amp; Payable</Typography>
                    {!header._is_interstate ? (
                      <>
                        <SynRow label="Total CGST" val={fmt(synopsis.totalCgst)} color="#2e7d32" />
                        <SynRow label="Total SGST" val={fmt(synopsis.totalSgst)} color="#2e7d32" />
                      </>
                    ) : (
                      <SynRow label="Total IGST" val={fmt(synopsis.totalIgst)} color="#2e7d32" />
                    )}
                    {synopsis.totalCess > 0 && <SynRow label="Total Cess" val={fmt(synopsis.totalCess)} color="#6a1b9a" />}
                    <SynRow label="Total Tax" val={fmt(synopsis.totalGst + synopsis.totalCess)} color="#2e7d32" />
                    {header.round_off_enabled !== 'N' && (
                      <>
                        <SynRow label="Sub Total" val={fmt(synopsis.rawNetPayable)} />
                        <SynRow
                          label="Round Off"
                          val={`${synopsis.roundOff >= 0 ? '+' : '−'}${fmt(Math.abs(synopsis.roundOff))}`}
                          color="#6d4c41"
                        />
                      </>
                    )}
                    <SynRow divTop label="Net Payable" val={fmt(synopsis.netPayable)} color={TEAL} bold large />
                    <Box sx={{ mt: 1, pt: 0.5 }}>
                      <FormControlLabel
                        control={<Switch size="small" checked={header.round_off_enabled !== 'N'} disabled={readOnly}
                          onChange={(e) => setHeader(p => ({ ...p, round_off_enabled: e.target.checked ? 'Y' : 'N' }))}
                          sx={{ '& .MuiSwitch-thumb': { bgcolor: header.round_off_enabled !== 'N' ? TEAL : undefined } }} />}
                        label={<Typography sx={{ fontSize: '0.72rem', color: '#666' }}>Round off net payable to nearest ₹</Typography>}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Card>
        )}

        {/* ── Payment section ── */}
        {invoice?.status === 'RECEIVED' && (
          <Card sx={{ overflow: 'hidden' }}>
            <SectionHeader
              title="Payment"
              action={paymentSummary?.payment_status && (
                <Chip label={paymentSummary.payment_status} size="small"
                  color={PAY_STATUS_COLOR[paymentSummary.payment_status] || 'default'}
                  sx={{ fontSize: '0.63rem', height: 18, fontWeight: 700, bgcolor: 'rgba(255,255,255,0.22)', color: 'white !important' }} />
              )}
            />
            <Box sx={{ p: 2.5 }}>

              {/* Stat tiles — using StatTile micro-component */}
              <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                <Grid item xs={6} md={3}>
                  <StatTile label="Amount Due" val={fmt(amountDue)} color="#333" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatTile label="Total Paid" val={fmt(totalPaid)} color="#2e7d32" bg="#f0fdf4" border="#c3e6cb" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatTile label={isSettleable ? 'Outstanding (Settleable)' : 'Outstanding'} val={fmt(outstanding)}
                    color={isSettleable ? '#2e7d32' : outstanding > 0.01 ? '#e65100' : '#2e7d32'}
                    bg={isSettleable ? '#f0fdf4' : outstanding > 0.01 ? '#fff8e1' : '#f0fdf4'}
                    border={isSettleable ? '#c3e6cb' : outstanding > 0.01 ? '#ffe082' : '#c3e6cb'} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatTile label="Transactions" val={String(paymentTxns.filter(t => t.status !== 'CANCELLED').length)} color="#555" />
                </Grid>
              </Grid>

              {/* Transactions table */}
              {paymentTxns.length > 0 && (
                <Box sx={{ mb: 2, border: '1px solid #eee', borderRadius: 1.5, overflow: 'hidden' }}>
                  <Table size="small">
                    <TableRow>
                      {['#', 'Date', 'Mode', 'UTR / Ref', 'Amount', 'Notes', 'Status', 'Actions'].map((h) => (
                        <TableCell key={h} sx={TH}>{h}</TableCell>
                      ))}
                    </TableRow>
                    <TableBody>
                      {paymentTxns.map((txn, idx) => (
                        <TableRow key={txn.id || idx} hover sx={{ opacity: txn.status === 'CANCELLED' ? 0.45 : 1 }}>
                          <TableCell sx={{ py: 1, px: 1.5, fontSize: '0.7rem', color: '#aaa' }}>{idx + 1}</TableCell>
                          <TableCell sx={{ py: 1, px: 1.5, fontSize: '0.78rem' }}>{txn.payment_date || '—'}</TableCell>
                          <TableCell sx={{ py: 1, px: 1.5 }}>
                            {txn.payment_mode
                              ? <Chip label={txn.payment_mode} size="small" sx={{ fontSize: '0.62rem', height: 18, bgcolor: '#f0f4ff', color: '#3949ab' }} />
                              : <Typography sx={{ fontSize: '0.72rem', color: '#ccc' }}>—</Typography>}
                          </TableCell>
                          <TableCell sx={{ py: 1, px: 1.5, fontSize: '0.72rem', fontFamily: 'monospace', color: '#666' }}>{txn.utr_number || '—'}</TableCell>
                          <TableCell sx={{ py: 1, px: 1.5, fontWeight: 700, fontSize: '0.82rem', color: txn.status === 'CANCELLED' ? '#bbb' : '#222', fontFamily: 'monospace' }}>{fmt(txn.amount)}</TableCell>
                          <TableCell sx={{ py: 1, px: 1.5, fontSize: '0.72rem', color: '#777', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{txn.notes || '—'}</TableCell>
                          <TableCell sx={{ py: 1, px: 1.5 }}>
                            <Chip label={txn.status || 'PAID'} size="small"
                              color={TXN_STATUS_COLOR[txn.status] || 'success'}
                              sx={{ fontSize: '0.62rem', height: 18, fontWeight: 700 }} />
                          </TableCell>
                          <TableCell sx={{ py: 1, px: 0.75 }}>
                            <Box sx={{ display: 'flex', gap: 0.25 }}>
                              <Tooltip title="Print Voucher">
                                <IconButton size="small" onClick={() => printSupplierPayment(txn, invoice, paymentTxns, selectedPharmacy)} sx={{ color: TEAL, '&:hover': { bgcolor: TEAL_LIGHT } }}>
                                  <PrintIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                              {txn.status !== 'CANCELLED' && invoice?.is_finalized !== 'Y' && (
                                <>
                                  <Tooltip title="Edit">
                                    <IconButton size="small" onClick={() => openEditPayment(txn)} sx={{ color: TEAL, '&:hover': { bgcolor: TEAL_LIGHT } }}>
                                      <EditIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton size="small" onClick={() => deletePaymentTxn(txn)} sx={{ color: '#ccc', '&:hover': { color: '#d33' } }}>
                                      <DeleteIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {/* Add / Edit payment form */}
              {showPaymentForm && invoice?.is_finalized !== 'Y' && (
                <Box sx={{ mb: 2, p: 2, bgcolor: `${TEAL}06`, border: `1px solid ${TEAL}22`, borderRadius: 1.5 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: TEAL, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {editingTxnId && editingTxnId !== 'legacy' ? 'Edit Payment Transaction' : 'Add Payment Transaction'}
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6} md={2}>
                      <FieldLabel label="Amount *">
                        <TextField size="small" fullWidth type="number" placeholder="0.00"
                          value={paymentForm.amount}
                          onChange={(e) => setPaymentForm(p => ({ ...p, amount: e.target.value }))}
                          sx={{ '& .MuiOutlinedInput-root': { height: 34, fontSize: '0.82rem' } }} />
                      </FieldLabel>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <FieldLabel label="Payment Mode *">
                        <Select size="small" fullWidth displayEmpty
                          value={paymentForm.payment_mode}
                          onChange={(e) => setPaymentForm(p => ({ ...p, payment_mode: e.target.value }))}
                          sx={{ height: 34, fontSize: '0.82rem' }}>
                          <MenuItem value=""><em>Select</em></MenuItem>
                          {['NEFT', 'RTGS', 'UPI', 'CHEQUE', 'CASH', 'DEMAND_DRAFT'].map(m => (
                            <MenuItem key={m} value={m}>{m}</MenuItem>
                          ))}
                        </Select>
                      </FieldLabel>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <FieldLabel label="Payment Date">
                        <MDDatePicker value={paymentForm.payment_date || null}
                          onChange={(v) => setPaymentForm(p => ({ ...p, payment_date: v || '' }))} />
                      </FieldLabel>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <FieldLabel label="UTR / Cheque No">
                        <TextField size="small" fullWidth
                          value={paymentForm.utr_number}
                          onChange={(e) => setPaymentForm(p => ({ ...p, utr_number: e.target.value.toUpperCase() }))}
                          sx={{ '& .MuiOutlinedInput-root': { height: 34, fontSize: '0.82rem' } }} />
                      </FieldLabel>
                    </Grid>
                    {!editingTxnId && (
                      <Grid item xs={12} sm={6} md={2}>
                        <FieldLabel label="POD / Receipt">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <input ref={podInputRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
                              onChange={(e) => setPodFile(e.target.files[0])} />
                            <Button size="small" variant="outlined" startIcon={<UploadFileIcon />}
                              sx={{ height: 34, textTransform: 'none', fontSize: '0.72rem', borderColor: '#ddd', color: '#555 !important' }}
                              onClick={() => podInputRef.current?.click()}>
                              {podFile ? podFile.name.substring(0, 12) + '…' : 'Upload'}
                            </Button>
                          </Box>
                        </FieldLabel>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={2}>
                      <FieldLabel label="Notes">
                        <TextField size="small" fullWidth
                          value={paymentForm.notes}
                          onChange={(e) => setPaymentForm(p => ({ ...p, notes: e.target.value.toUpperCase() }))}
                          sx={{ '& .MuiOutlinedInput-root': { height: 34, fontSize: '0.82rem' } }} />
                      </FieldLabel>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                    <Button variant="contained" size="small"
                      sx={{ bgcolor: TEAL, color: 'white !important', '&:hover': { bgcolor: '#005e6e', color: 'white !important' }, textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
                      onClick={savePaymentTxn} disabled={savingPayment}>
                      {savingPayment ? 'Saving…' : (editingTxnId && editingTxnId !== 'legacy' ? 'Update Payment' : 'Save Payment')}
                    </Button>
                    <Button variant="outlined" size="small"
                      sx={{ textTransform: 'none', color: '#666 !important', borderColor: '#ccc' }}
                      onClick={closePaymentForm}>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Action bar */}
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {!showPaymentForm && invoice?.is_finalized !== 'Y' && (
                  <Button variant="outlined" size="small" startIcon={<AddIcon />}
                    sx={{ textTransform: 'none', color: `${TEAL} !important`, borderColor: TEAL, fontWeight: 600, '&:hover': { bgcolor: TEAL_LIGHT } }}
                    onClick={openAddPayment}>
                    Add Payment
                  </Button>
                )}
                {isFullyPaid && invoice?.is_finalized !== 'Y' && (
                  <Button variant="contained" size="small" startIcon={<LockIcon />}
                    sx={{ bgcolor: '#2e7d32', color: 'white !important', '&:hover': { bgcolor: '#1b5e20', color: 'white !important' }, textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
                    onClick={finalizeInvoice}>
                    Finalize &amp; Close Invoice
                  </Button>
                )}
                {isSettleable && invoice?.is_finalized !== 'Y' && (
                  <Tooltip title={`Outstanding ₹${outstanding.toFixed(2)} is within the settlement limit (≤ min 0.5%, ₹100)`}>
                    <Button variant="outlined" size="small" startIcon={<HandshakeOutlinedIcon />}
                      sx={{ borderColor: '#2e7d32', color: '#2e7d32 !important', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#f0fdf4' } }}
                      onClick={settleInvoice} disabled={saving}>
                      Settle &amp; Close (₹{outstanding.toFixed(2)})
                    </Button>
                  </Tooltip>
                )}
                {invoice?.is_finalized === 'Y' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#2e7d32' }}>
                    <LockIcon sx={{ fontSize: 16 }} />
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>Invoice finalized and closed</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Card>
        )}
      </MDBox>
      <Footer />

      <PriceBreakdownDialog
        open={Boolean(priceDialogRow)}
        onClose={() => setPriceDialogRow(null)}
        row={priceDialogRow}
        isInterstate={header._is_interstate}
      />

      {/* ── GRN Documents Dialog ── */}
      <Dialog open={showDocsDialog} onClose={() => !uploadingDoc && setShowDocsDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ background: `linear-gradient(135deg, #5c6bc0 0%, #3949ab 100%)`, px: 2.5, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'white !important' }}>GRN Documents</Typography>
              <Typography sx={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.78) !important', mt: 0.3 }}>
                {invoice?.inward_no} · Supplier invoices, delivery challans &amp; other files
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setShowDocsDialog(false)} disabled={uploadingDoc} sx={{ color: 'rgba(255,255,255,0.7)' }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: '20px !important' }}>
          {/* Upload row */}
          {invoice?.status !== 'CANCELLED' && (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2.5, p: 1.5, bgcolor: '#f8f9ff', border: '1px solid #e8eaf6', borderRadius: 1.5 }}>
              <Select size="small" value={docType} onChange={e => setDocType(e.target.value)} sx={{ height: 34, fontSize: '0.82rem', minWidth: 180 }}>
                <MenuItem value="SUPPLIER_GRN">Supplier GRN / Invoice</MenuItem>
                <MenuItem value="DELIVERY_CHALLAN">Delivery Challan</MenuItem>
                <MenuItem value="QUALITY_REPORT">Quality / QC Report</MenuItem>
                <MenuItem value="OTHER">Other Document</MenuItem>
              </Select>
              <input ref={docFileRef} type="file" accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                style={{ display: 'none' }} onChange={handleDocUpload} />
              <Button variant="contained" size="small" startIcon={uploadingDoc ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <UploadFileIcon />}
                disabled={uploadingDoc}
                sx={{ bgcolor: '#5c6bc0', color: 'white !important', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#3949ab' }, boxShadow: 'none', height: 34 }}
                onClick={() => docFileRef.current?.click()}>
                {uploadingDoc ? 'Uploading…' : 'Upload File'}
              </Button>
              <Typography sx={{ fontSize: '0.7rem', color: '#999' }}>PDF, images, Word, Excel</Typography>
            </Box>
          )}

          {/* File list */}
          {grnDocs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5, color: '#bbb' }}>
              <FolderOpenIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
              <Typography sx={{ fontSize: '0.85rem' }}>No documents uploaded yet</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {grnDocs.map((doc) => {
                const isImage = doc.file_type?.startsWith('image/');
                const isPdf = doc.file_type === 'application/pdf';
                const docTypeLabel = { SUPPLIER_GRN: 'Supplier GRN', DELIVERY_CHALLAN: 'Delivery Challan', QUALITY_REPORT: 'QC Report', OTHER: 'Other' }[doc.document_type] || doc.document_type;
                return (
                  <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, border: '1px solid #eee', borderRadius: 1.5, '&:hover': { bgcolor: '#fafafa' } }}>
                    {/* Preview thumbnail */}
                    {isImage ? (
                      <Box component="img" src={doc.file_url} alt={doc.file_name}
                        sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd', cursor: 'pointer', flexShrink: 0 }}
                        onClick={() => window.open(doc.file_url, '_blank')} />
                    ) : (
                      <Box sx={{ width: 56, height: 56, bgcolor: isPdf ? '#ffebee' : '#e8f5e9', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, border: '1px solid #ddd' }}
                        onClick={() => window.open(doc.file_url, '_blank')}>
                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: isPdf ? '#c62828' : '#2e7d32', textAlign: 'center' }}>
                          {isPdf ? 'PDF' : 'DOC'}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.file_name}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.3, alignItems: 'center' }}>
                        <Chip label={docTypeLabel} size="small" sx={{ fontSize: '0.6rem', height: 16, bgcolor: '#e8eaf6', color: '#3949ab' }} />
                        <Typography sx={{ fontSize: '0.65rem', color: '#aaa' }}>{new Date(doc.created_at).toLocaleDateString('en-IN')}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                      <Tooltip title="Open in new tab">
                        <IconButton size="small" onClick={() => window.open(doc.file_url, '_blank')} sx={{ color: '#5c6bc0' }}>
                          <FolderOpenIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      {invoice?.status !== 'CANCELLED' && (
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDocDelete(doc.id)} sx={{ color: '#ccc', '&:hover': { color: '#d33' } }}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setShowDocsDialog(false)} sx={{ textTransform: 'none', color: '#555 !important' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── GRN Return Dialog ── */}
      <Dialog open={showReturnDialog} onClose={() => !savingReturn && setShowReturnDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ background: `linear-gradient(135deg, #e65100 0%, #bf360c 100%)`, px: 2.5, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'white !important' }}>Return Items to Supplier</Typography>
              <Typography sx={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.78) !important', mt: 0.3 }}>{invoice?.inward_no} · {invoice?.supplier?.name}</Typography>
            </Box>
            <IconButton size="small" onClick={() => setShowReturnDialog(false)} disabled={savingReturn} sx={{ color: 'rgba(255,255,255,0.7)' }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', mb: 0.5 }}>Return Date</Typography>
              <TextField type="date" size="small" fullWidth value={returnDate} onChange={e => setReturnDate(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.82rem', height: 36 } }} />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', mb: 0.5 }}>Reason *</Typography>
              <TextField size="small" fullWidth value={returnReason} onChange={e => setReturnReason(e.target.value)}
                placeholder="Reason for return (damaged, expired, short supply, etc.)"
                sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.82rem', height: 36 } }} />
            </Grid>
          </Grid>

          <TableContainer sx={{ border: '1px solid #eee', borderRadius: 1.5 }}>
            <Table size="small">
              <TableBody>
                <TableRow sx={{ bgcolor: '#e65100' }}>
                  <TableCell sx={{ ...TH, color: 'white !important', width: '40%' }}>Item</TableCell>
                  <TableCell sx={{ ...TH, color: 'white !important', width: '15%', textAlign: 'right' }}>Batch</TableCell>
                  <TableCell sx={{ ...TH, color: 'white !important', width: '15%', textAlign: 'right' }}>Received</TableCell>
                  <TableCell sx={{ ...TH, color: 'white !important', width: '15%', textAlign: 'right' }}>Returned</TableCell>
                  <TableCell sx={{ ...TH, color: 'white !important', width: '15%', textAlign: 'right' }}>Return Qty</TableCell>
                </TableRow>
                {returnLines.map((line, i) => (
                  <TableRow key={line.id} sx={{ bgcolor: line.available <= 0 ? '#fafafa' : 'white', opacity: line.available <= 0 ? 0.5 : 1 }}>
                    <TableCell sx={{ ...TD, py: 0.8, pl: 1, fontSize: '0.8rem', fontWeight: 600 }}>{line.name}</TableCell>
                    <TableCell sx={{ ...TD, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.72rem', color: '#777' }}>{line.batch_no || '—'}</TableCell>
                    <TableCell sx={{ ...TD, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.78rem' }}>{line.qty_received}</TableCell>
                    <TableCell sx={{ ...TD, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.78rem', color: line.already_returned > 0 ? '#e65100' : '#bbb' }}>
                      {line.already_returned > 0 ? line.already_returned : '—'}
                    </TableCell>
                    <TableCell sx={{ ...TD, textAlign: 'right', py: 0.25, px: 0.5 }}>
                      <TextField
                        size="small" type="number" disabled={line.available <= 0}
                        value={line.qty_return}
                        onChange={e => setReturnLines(prev => prev.map((l, j) => j === i ? { ...l, qty_return: e.target.value } : l))}
                        inputProps={{ min: 0, max: line.available, step: 1, style: { textAlign: 'right', fontSize: '0.82rem', padding: '4px 6px' } }}
                        sx={{ width: 90, '& .MuiOutlinedInput-root': { height: 28, borderRadius: 1, fontSize: '0.82rem' } }}
                        placeholder={line.available <= 0 ? 'Fully returned' : `Max ${line.available}`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {existingReturns.filter(r => r.status !== 'CANCELLED').length > 0 && (
            <Alert severity="warning" sx={{ mt: 2, fontSize: '0.8rem' }}>
              Prior returns exist for this GRN — available quantities already reflect returned stock.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, borderTop: '1px solid #f0f0f0', gap: 1 }}>
          <Button onClick={() => setShowReturnDialog(false)} disabled={savingReturn}
            sx={{ textTransform: 'none', color: '#555 !important' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitReturn} disabled={savingReturn}
            sx={{ bgcolor: '#e65100', color: 'white !important', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#bf360c' }, boxShadow: 'none' }}>
            {savingReturn ? 'Processing…' : 'Confirm Return'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── GRN Timeline ── */}
      <TimelineDialog
        open={showTimeline}
        onClose={() => setShowTimeline(false)}
        title="GRN Journey"
        subtitle={invoice ? `${invoice.inward_no} · ${invoice.supplier?.name || ''}` : ''}
        events={timelineEvents}
        loading={timelineLoading}
      />

      {/* ── Inline quick-add: Supplier / Location / Material ── */}
      <SupplierQuickAdd
        open={quickAdd?.kind === 'supplier'}
        initialName={quickAdd?.name || ''}
        onClose={closeQuickAdd}
        onCreated={onSupplierCreated}
      />
      <LocationQuickAdd
        open={quickAdd?.kind === 'location'}
        initialName={quickAdd?.name || ''}
        pharmacy={selectedPharmacy}
        onClose={closeQuickAdd}
        onCreated={onLocationCreated}
      />
      <ItemQuickAdd
        open={quickAdd?.kind === 'item'}
        initialName={quickAdd?.name || ''}
        onClose={closeQuickAdd}
        onCreated={onItemCreated}
      />
    </DashboardLayout>
  );
}

export default InwardForm;
